// @ts-check

/**
 * @typedef {Object} AdminImage
 * @property {string} id
 * @property {string} title
 * @property {string} [description]
 * @property {string} filename
 * @property {string} image_url
 * @property {string|null} thumbnail_url
 * @property {string} category
 * @property {string[]} tags
 * @property {number} width
 * @property {number} height
 * @property {number} is_active
 * @property {number} [is_featured]
 * @property {number} popularity
 * @property {number} [is_user_contributed]
 * @property {number|null} [is_nsfw]
 * @property {string|null} [user_id]
 * @property {number} created_at
 * @property {number} updated_at
 * @property {number|null} [deleted_at]
 */

import { fillGridPresetSelects, resolveStoredGridPreset } from './grid-preset-selects.js';
import { adminBroadcastResult } from './chat-broadcast.js';
import { initBulkUpload } from './bulk-upload.js';
import { bindPageHelpToggles } from './page-help.js';
import { API_BASE, resolveAdminMediaUrl, resolveInitialAdminToken } from './config.js';
import { syncLobbyVisibilityToggle } from './lobby-visibility-toggle.js';
import 'flag-icons/css/flag-icons.min.css';
import { ADMIN_PAGE_LIMIT, buildPageMeta, renderPaginationHtml } from './pagination.js';
import {
  DEFAULT_CATEGORIES,
  sanitizeCategoryName,
  UNCATEGORIZED_CATEGORY,
  sanitizeTagList,
  sanitizeTagName
} from '../../shared/catalog.js';
import {
  GENERATED_JPEG_QUALITY,
  GENERATED_PUZZLE_HEIGHT,
  GENERATED_PUZZLE_WIDTH,
  GENERATED_SHARPEN_AMOUNT,
  unsharpMask,
  upscaleSteps
} from '../../shared/generated-image.js';
import {
  CATALOG_ACTIONS,
  catalogRowHtml,
  categoryOptionsHtml,
  defaultUploadCategory,
  tagSuggestItems
} from './catalog-options.js';
import { editSubmitLabel, editSubmitPendingLabel } from './submit-label.js';
import { initColumnChoosers } from './columns.js';
import { applyBotHostingNav, applyErrorCollectionNav, applySupportMessagesNav, initAdminNav, isDefaultOffNavVisible, isDefaultOnNavVisible, isSupportMessagesNavVisible, syncNavCurrentLabel } from './nav.js';
import { ratingBadgeHtml, ratingLabel, publishToggleButtonHtml, featureToggleButtonHtml, imageSourceHtml } from './image-badges.js';
import { escapeHtml, tableActionsHtml } from './html.js';
import { adminRoomCompletedAt, adminRoomStatus, formatActiveTotalPlayers } from './rooms-display.js';
import { adminRoomVisibilityHtml, roomPublishInstructions } from './room-visibility.js';
import { difficultyLabel } from '../../shared/difficulty.js';
import { progressDoughnutHtml } from '../../shared/room-progress.js';
import { NUMERIC_SETTING_BOUNDS, clampNumericSetting, numericSettingHint } from '../../shared/settings-bounds.js';
import { matchesSearch } from '../../shared/search-text.js';
import { createBrowserErrorReporter } from '../../shared/error-reporter.js';
import {
  SPARKLES_ICON_SVG,
  DICE_ICON_SVG,
  HOURGLASS_ICON_SVG,
  WAND_ICON_SVG,
  SKIP_ICON_SVG,
  HAND_ICON_SVG
} from '../../shared/icons.js';

import { tierCeilingHint, tierCeilingWarning } from './rate-limit-hints.js';
import {
  ADMIN_SESSION_STORAGE_KEY,
  readAdminSession,
  sessionFromLoginResponse,
  writeAdminSession
} from './admin-session.js';
import { refreshDashboard, syncDashboardPolling, initDashboardControls } from './dashboard.js';

// Dashboard failures are reported into the same log the Errors tab renders.
createBrowserErrorReporter({
  endpoint: API_BASE + '/api/errors',
  source: 'admin',
  win: typeof window !== 'undefined' ? window : null
});

/** @type {AdminImage[]} */
let allImages = [];
/** @type {File|null} */
let editSelectedFile = null;
/** @type {string|null} */
let selectedImportUrl = null;
let initialEditActive = 1;

/** @type {'grid'|'table'} */
let imagesViewMode = 'grid';
let imagesPage = 1;
/** @type {import('./pagination.js').AdminPageMeta} */
let imagesPageMeta = buildPageMeta(0, 1);
let imagesLoading = false;
let roomsPage = 1;
/** @type {import('./pagination.js').AdminPageMeta} */
let roomsPageMeta = buildPageMeta(0, 1);
let roomsLoading = false;
let usersPage = 1;
/** @type {import('./pagination.js').AdminPageMeta} */
let usersPageMeta = buildPageMeta(0, 1);
let usersLoading = false;
let flagsPage = 1;
/** @type {import('./pagination.js').AdminPageMeta} */
let flagsPageMeta = buildPageMeta(0, 1);
let flagsLoading = false;
let botPage = 1;
/** @type {import('./pagination.js').AdminPageMeta} */
let botPageMeta = buildPageMeta(0, 1);
let botLoading = false;
let errorsPage = 1;
/** @type {import('./pagination.js').AdminPageMeta} */
let errorsPageMeta = buildPageMeta(0, 1);
let errorsLoading = false;
let conversationsPage = 1;
/** @type {import('./pagination.js').AdminPageMeta} */
let conversationsPageMeta = buildPageMeta(0, 1);
let conversationsLoading = false;
/** User id of the conversation open in the modal, empty when closed. */
let activeConversationUserId = '';
/** @type {ReturnType<typeof setInterval>|null} */
let botPollTimer = null;

/** @type {Array<{id: string, name: string}>} */
let catalogCategories = [];
/** @type {Array<{id: string, name: string}>} */
let catalogTags = [];
/** Snapshot of the saved settings form, used for dirty tracking. */
let settingsSnapshot = '';

/**
 * Shows or clears a dropzone preview and keeps its placeholder in sync.
 *
 * @param {HTMLImageElement|null} previewImg
 * @param {string} src - Image source, or `''` to fall back to the placeholder.
 */
function setDropzonePreview(previewImg, src) {
  if (!previewImg) return;
  const dropzone = previewImg.closest('.dropzone');
  const resolved = resolveAdminMediaUrl(src);
  previewImg.src = resolved;
  previewImg.style.display = src ? 'inline-block' : 'none';
  dropzone?.classList.toggle('has-image', Boolean(src));
}

/**
 * Makes a dropzone keyboard operable: Space or Enter opens the file picker.
 *
 * @param {HTMLElement|null} dropzone
 * @param {HTMLInputElement|null} fileInput
 */
function initDropzoneKeyboard(dropzone, fileInput) {
  dropzone?.addEventListener('keydown', (e) => {
    if (e.key !== ' ' && e.key !== 'Spacebar' && e.key !== 'Enter') return;
    e.preventDefault();
    fileInput?.click();
  });
}

/**
 * Reads a file as a data URL for image analysis requests.
 *
 * @param {File} file
 * @returns {Promise<string>}
 */
function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(new Error('Could not read image file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Writes a table cell with a mobile stacked-card label.
 * @param {string} label
 * @param {string} innerHtml
 * @param {string} [attrs]
 * @returns {string}
 */
function tableCell(label, innerHtml, attrs = '') {
  return '<td data-label="' + escapeHtml(label) + '"' + (attrs ? ' ' + attrs : '') + '>' + innerHtml + '</td>';
}

/**
 * Formats a stored timestamp for table cells.
 * @param {unknown} value
 * @returns {string}
 */
function formatAdminDate(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return new Date(n).toLocaleString();
}

/**
 * Shortens long text for dense table cells.
 * @param {unknown} value
 * @param {number} [max]
 * @returns {string}
 */
function truncateText(value, max = 80) {
  const text = String(value ?? '').trim();
  if (!text) return '—';
  return text.length > max ? text.slice(0, Math.max(1, max - 1)) + '…' : text;
}

/**
 * Escaped monospace id cell, or an em dash when empty.
 * @param {unknown} value
 * @returns {string}
 */
function monoIdHtml(value) {
  const text = String(value ?? '').trim();
  if (!text) return '<span class="muted-dash">—</span>';
  return '<span class="mono">' + escapeHtml(text) + '</span>';
}

/**
 * Renders AJAX page controls into a nav and keeps the current page in sync.
 * @param {string} navId
 * @param {import('./pagination.js').AdminPageMeta} meta
 * @param {(page: number) => void} onPage
 */
function mountPagination(navId, meta, onPage) {
  const nav = document.getElementById(navId);
  if (!nav) return;
  const resolved = buildPageMeta(meta.total, meta.page, meta.pageSize || ADMIN_PAGE_LIMIT);
  nav.innerHTML = renderPaginationHtml(resolved);
  nav.onclick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const btn = target.closest('[data-page]');
    if (!btn || !(btn instanceof HTMLButtonElement) || btn.disabled) return;
    const nextPage = Number(btn.getAttribute('data-page'));
    if (!Number.isFinite(nextPage) || nextPage < 1 || nextPage === resolved.page) return;
    onPage(nextPage);
  };
}

/**
 * Reads list pagination fields from an admin API payload.
 * @param {any} data
 * @param {number} fallbackPage
 * @param {number} itemCount
 * @returns {import('./pagination.js').AdminPageMeta}
 */
function metaFromResponse(data, fallbackPage, itemCount) {
  const total = Number(data?.total);
  return buildPageMeta(
    Number.isFinite(total) ? total : itemCount,
    Number(data?.page) || fallbackPage,
    Number(data?.pageSize) || ADMIN_PAGE_LIMIT
  );
}

/**
 * Bearer token for admin API calls: the dashboard session, never the raw secret.
 *
 * @returns {string}
 */
function getAuthToken() {
  return readAdminSession(localStorage)?.token || '';
}

/**
 * Shows the sign-in gate, optionally with a reason, and focuses the key field.
 *
 * @param {string} [message]
 * @returns {void}
 */
function showLoginGate(message = '') {
  const gate = document.getElementById('admin-login-gate');
  const error = document.getElementById('admin-login-error');
  const input = /** @type {HTMLInputElement|null} */ (document.getElementById('admin-login-key'));
  if (error) {
    error.textContent = message;
    error.hidden = !message;
  }
  if (gate) gate.hidden = false;
  if (input) {
    if (!input.value) input.value = resolveInitialAdminToken();
    input.focus();
    input.select();
  }
}

/** Hides the sign-in gate and clears the typed key from the DOM. */
function hideLoginGate() {
  const gate = document.getElementById('admin-login-gate');
  const input = /** @type {HTMLInputElement|null} */ (document.getElementById('admin-login-key'));
  if (gate) gate.hidden = true;
  if (input) input.value = '';
}

/**
 * Drops the session and returns to the gate. Used by Sign out and by any 401.
 *
 * @param {string} [message]
 * @returns {void}
 */
function endAdminSession(message = '') {
  const token = getAuthToken();
  try {
    localStorage.removeItem(ADMIN_SESSION_STORAGE_KEY);
  } catch {
    // Nothing to recover from: the gate is shown either way.
  }
  fetch(API_BASE + '/api/admin/logout', {
    method: 'POST',
    credentials: 'include',
    headers: token ? { Authorization: 'Bearer ' + token } : {}
  }).catch(() => { });
  showLoginGate(message);
}

/**
 * Reloads whichever tab is open, so signing in fills the dashboard immediately.
 * @returns {void}
 */
function refreshActiveTab() {
  if (activeTab === 'dashboard') refreshDashboard();
  else if (activeTab === 'rooms') refreshRooms();
  else if (activeTab === 'users') refreshUsers();
  else if (activeTab === 'catalog') fetchCatalog();
  else if (activeTab === 'flagged') refreshFlaggedMessages();
  else if (activeTab === 'errors') refreshClientErrors();
  else if (activeTab === 'messages') refreshConversations();
  else if (activeTab === 'settings') loadSettings();
  else refreshImages();
}

/**
 * Exchanges the typed secret for a session token.
 *
 * @param {string} key
 * @returns {Promise<void>}
 */
async function submitAdminLogin(key) {
  const submitBtn = /** @type {HTMLButtonElement|null} */ (document.getElementById('admin-login-submit'));
  const secret = String(key || '').trim();
  if (!secret) {
    showLoginGate('Enter the admin secret key.');
    return;
  }
  if (submitBtn) submitBtn.disabled = true;
  try {
    const res = await fetch(API_BASE + '/api/admin/login', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key: secret })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const reason = res.status === 429
        ? 'Too many attempts. Wait ' + (Number(data.retryAfterSeconds) || 60) + 's and try again.'
        : (data.error || 'Sign-in failed.');
      showLoginGate(reason);
      return;
    }
    const session = sessionFromLoginResponse(data);
    if (!session) {
      showLoginGate('The server did not return a session token.');
      return;
    }
    writeAdminSession(localStorage, session);
    hideLoginGate();
    refreshActiveTab();
  } catch (err) {
    showLoginGate(err instanceof Error ? err.message : 'Sign-in failed.');
  } finally {
    if (submitBtn) submitBtn.disabled = false;
  }
}

/**
 * Sends every expired or rejected admin call back to the gate.
 *
 * One interceptor rather than a check at each of the ~40 admin fetch sites: a
 * missed site would leave the dashboard silently showing stale, empty tables.
 * @returns {void}
 */
function installUnauthorizedInterceptor() {
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const response = await originalFetch(input, init);
    const url = typeof input === 'string' ? input : (input instanceof Request ? input.url : String(input));
    const isAdminApiCall = url.startsWith(API_BASE + '/api/admin/') && !url.endsWith('/api/admin/login');
    if (response.status === 401 && isAdminApiCall) {
      endAdminSession('That session has expired. Sign in again.');
    }
    return response;
  };
}

/** Wires the sign-in form and the Sign out button. */
function initAdminLogin() {
  const form = document.getElementById('admin-login-form');
  form?.addEventListener('submit', (event) => {
    event.preventDefault();
    const input = /** @type {HTMLInputElement|null} */ (document.getElementById('admin-login-key'));
    submitAdminLogin(input?.value || '');
  });
  document.getElementById('admin-sign-out-btn')?.addEventListener('click', () => {
    endAdminSession();
  });
}

function showToast(message, type = 'success') {
  const toast = document.getElementById('toast');
  if (!toast) return;
  toast.textContent = message;
  toast.className = 'toast show ' + type;
  setTimeout(() => {
    toast.className = 'toast';
  }, 3500);
}

/**
 * Resets image pagination and fetches the first page.
 * @returns {Promise<void>}
 */
function refreshImages() {
  imagesPage = 1;
  return fetchImages();
}

/**
 * Fetches one page of admin images via AJAX.
 */
async function fetchImages() {
  if (imagesLoading) return;

  const token = getAuthToken();
  const searchInput = /** @type {HTMLInputElement|null} */ (document.getElementById('search-filter'));
  const categorySelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('category-filter'));
  const statusSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('image-status-filter'));
  const uploadedBySelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('image-uploaded-by-filter'));
  const nsfwSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('image-nsfw-filter'));

  const q = searchInput?.value.trim() || '';
  const category = categorySelect?.value || 'All';
  const status = statusSelect?.value || 'all';
  const uploadedBy = uploadedBySelect?.value || 'all';
  const nsfw = nsfwSelect?.value || 'all';

  const params = new URLSearchParams({
    q,
    category,
    status,
    uploaded_by: uploadedBy,
    nsfw,
    limit: String(ADMIN_PAGE_LIMIT),
    page: String(imagesPage)
  });
  if (q.startsWith('img_') || q.startsWith('id:')) {
    params.set('id', q.replace(/^id:\s*/i, ''));
  }

  imagesLoading = true;
  try {
    const res = await fetch(API_BASE + '/api/admin/images?' + params.toString(), {
      headers: {
        'Authorization': 'Bearer ' + token
      }
    });

    if (res.status === 401) {
      showToast('Unauthorized: Check Admin Secret Key', 'error');
      return;
    }

    const data = await res.json();
    const page = data.images || [];
    imagesPageMeta = metaFromResponse(data, imagesPage, page.length);
    imagesPage = imagesPageMeta.page;
    allImages = page;
    renderImages();
    mountPagination('images-pagination', imagesPageMeta, (next) => {
      imagesPage = next;
      fetchImages();
    });
  } catch (err) {
    console.error('Failed to fetch images:', err);
    showToast('Failed to load images from server', 'error');
  } finally {
    imagesLoading = false;
  }
}

/**
 * Applies grid/table visibility and re-renders the active images view.
 */
function renderImages() {
  applyImagesViewMode();
  renderGallery(allImages);
  renderImagesTable(allImages);
}

/**
 * Toggles grid vs table containers and button active states.
 */
function applyImagesViewMode() {
  const grid = document.getElementById('images-container');
  const table = document.getElementById('images-table-container');
  const gridBtn = document.getElementById('images-view-grid-btn');
  const tableBtn = document.getElementById('images-view-table-btn');
  const colChooser = document.querySelector('[data-column-chooser="images"]');

  if (imagesViewMode === 'table') {
    grid?.classList.add('view-hidden');
    grid?.classList.remove('view-active');
    table?.classList.add('view-active');
    table?.classList.remove('view-hidden');
    gridBtn?.classList.remove('active');
    tableBtn?.classList.add('active');
    colChooser?.classList.remove('view-hidden');
  } else {
    grid?.classList.remove('view-hidden');
    grid?.classList.add('view-active');
    table?.classList.remove('view-active');
    table?.classList.add('view-hidden');
    gridBtn?.classList.add('active');
    tableBtn?.classList.remove('active');
    colChooser?.classList.add('view-hidden');
  }
}

/**
 * @param {'grid'|'table'} mode
 */
function setImagesViewMode(mode) {
  imagesViewMode = mode === 'table' ? 'table' : 'grid';
  applyImagesViewMode();
}

/**
 * @param {AdminImage|null|undefined} img
 * @returns {boolean}
 */
function imageIsDeleted(img) {
  return Number(img?.deleted_at) > 0;
}

/**
 * Delete hides the image. Restore puts a deleted image back.
 * @param {AdminImage} img
 * @param {string} extraClass
 * @returns {string}
 */
function imageRemovalButtonHtml(img, extraClass) {
  if (imageIsDeleted(img)) {
    return '<button type="button" class="btn btn-secondary btn-sm restore-btn ' + extraClass + '" data-id="' + img.id + '">Restore</button>';
  }
  return '<button type="button" class="btn btn-danger btn-sm delete-btn ' + extraClass + '" data-id="' + img.id + '">Delete</button>';
}

/**
 * Renders the gallery cards into the DOM.
 * @param {AdminImage[]} images
 */
function renderGallery(images) {
  const container = document.getElementById('images-container');
  const countText = document.getElementById('image-count-text');
  if (!container) return;

  if (countText) {
    countText.textContent = images.length + ' image' + (images.length === 1 ? '' : 's');
  }

  if (images.length === 0) {
    container.innerHTML = '<div class="gallery-empty">No puzzle images found matching filters.</div>';
    return;
  }

  container.innerHTML = images.map((img) => {
    const tagsHtml = (img.tags || []).slice(0, 4).map((t) => escapeHtml(t)).join(', ');
    const dateStr = new Date(img.created_at).toLocaleDateString();

    const ratingBadge = ratingBadgeHtml(img.is_nsfw);
    const uId = img.user_id ? String(img.user_id).trim() : '';
    const sourceMeta = img.is_user_contributed
      ? (uId
          ? '<span title="Contributed by player ' + escapeHtml(uId) + '">User <span class="mono">(' + escapeHtml(uId) + ')</span></span>'
          : '<span title="Contributed by a player">User</span>')
      : '';

    return (
      '<div class="image-card" data-id="' + img.id + '">' +
      '<div class="card-thumbnail-wrap">' +
      '<img src="' + escapeHtml(resolveAdminMediaUrl(img.thumbnail_url || img.image_url)) + '" alt="' + escapeHtml(img.title) + '" loading="lazy" />' +
      '<span class="card-category-badge">' + escapeHtml(img.category) + '</span>' +
      featureToggleButtonHtml(img, 'image-grid-feature-btn', 'star') +
      ratingBadge +
      '</div>' +
      '<div class="card-content">' +
      '<div class="card-title" title="' + escapeHtml(img.title) + '">' +
      escapeHtml(img.title) +
      '</div>' +
      '<div class="card-meta">' +
      '<span>' + (img.width || 1920) + ' &times; ' + (img.height || 1080) + '</span>' +
      '<span>' + dateStr + '</span>' +
      sourceMeta +
      '</div>' +
      '<div class="card-tags">' +
      tagsHtml +
      '</div>' +
      '<div class="card-actions">' +
      '<div class="card-actions-primary">' +
      publishToggleButtonHtml(img) +
      '</div>' +
      '<div class="row-actions">' +
      '<button class="btn btn-secondary btn-sm edit-btn" data-id="' + img.id + '">Edit</button>' +
      imageRemovalButtonHtml(img, '') +
      '</div>' +
      '</div>' +
      '</div>' +
      '</div>'
    );
  }).join('');

  attachCardEvents();
}

/**
 * Renders the images table view.
 * @param {AdminImage[]} images
 */
function renderImagesTable(images) {
  const tbody = document.getElementById('images-table-body');
  const countText = document.getElementById('image-count-text');
  if (!tbody) return;

  if (countText) {
    const total = imagesPageMeta.total;
    countText.textContent = total + ' image' + (total === 1 ? '' : 's');
  }

  if (images.length === 0) {
    tbody.innerHTML = '<tr class="table-empty-row"><td colspan="15" data-label="" class="cell-empty">No puzzle images found matching filters.</td></tr>';
    return;
  }

  tbody.innerHTML = images.map((img) => {
    const deleted = imageIsDeleted(img);
    const statusClass = deleted ? 'status-inactive' : (img.is_active ? 'status-active' : 'status-inactive');
    const statusLabel = deleted ? 'Deleted' : (img.is_active ? 'Active' : 'Inactive');
    const dateStr = new Date(img.created_at).toLocaleDateString();
    const updatedStr = img.updated_at ? new Date(img.updated_at).toLocaleDateString() : '—';
    const tagsText = (img.tags || []).slice(0, 6).join(', ') || '—';
    const nsfwLabel = ratingLabel(img.is_nsfw);
    const thumb = escapeHtml(resolveAdminMediaUrl(img.thumbnail_url || img.image_url || ''));
    const sizeLabel = (img.width || 0) && (img.height || 0)
      ? (img.width + '×' + img.height)
      : '—';
    const sourceLabel = imageSourceHtml(img, monoIdHtml);
    const description = truncateText(img.description || '', 100);

    return (
      '<tr>' +
      tableCell('Preview', '<img src="' + thumb + '" alt="" class="table-thumb" loading="lazy" />') +
      tableCell('Title', escapeHtml(img.title), 'class="cell-title"') +
      tableCell('ID', monoIdHtml(img.id)) +
      tableCell('Description', escapeHtml(description), 'class="cell-clip" title="' + escapeHtml(img.description || '') + '"') +
      tableCell('Category', escapeHtml(img.category)) +
      tableCell('Tags', escapeHtml(tagsText), 'class="cell-nowrap" title="' + escapeHtml(tagsText) + '"') +
      tableCell('Size', escapeHtml(sizeLabel), 'class="cell-meta-nowrap"') +
      tableCell('Popularity', String(Number(img.popularity) || 0), 'class="cell-meta"') +
      tableCell('Source', sourceLabel, 'class="cell-meta"') +
      tableCell('Status', '<span class="card-status-badge ' + statusClass + ' badge-inline-label">' + statusLabel + '</span>') +
      tableCell('Rating', nsfwLabel, 'class="cell-meta"') +
      tableCell('Created', dateStr, 'class="cell-date"') +
      tableCell('Updated', updatedStr, 'class="cell-date"') +
      tableCell('Star', featureToggleButtonHtml(img, 'image-table-feature-btn'), 'class="image-star-cell"') +
      tableCell('Actions',
        tableActionsHtml(
          '<button type="button" class="btn btn-secondary btn-sm image-table-edit-btn" data-id="' + img.id + '">Edit</button>' +
          imageRemovalButtonHtml(img, 'image-table-delete-btn')
        )) +
      '</tr>'
    );
  }).join('');

  tbody.querySelectorAll('.image-table-edit-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const id = /** @type {HTMLElement} */ (e.currentTarget).getAttribute('data-id');
      const img = allImages.find((item) => item.id === id);
      if (img) openEditModal(img);
    });
  });

  tbody.querySelectorAll('.image-table-delete-btn.delete-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const id = /** @type {HTMLElement} */ (e.currentTarget).getAttribute('data-id');
      if (id) deleteAdminImage(id);
    });
  });
  tbody.querySelectorAll('.image-table-delete-btn.restore-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const id = /** @type {HTMLElement} */ (e.currentTarget).getAttribute('data-id');
      if (id) restoreAdminImage(id);
    });
  });
  attachCardEvents();
}

/**
 * Clone is only available while an existing image is open.
 * @param {boolean} visible
 */
function setCloneButtonVisible(visible) {
  const btn = document.getElementById('edit-clone-btn');
  if (btn instanceof HTMLButtonElement) btn.hidden = !visible;
}

/**
 * Copies the image open in the editor into a new unpublished draft and loads it.
 * @returns {Promise<void>}
 */
async function cloneEditingImage() {
  const idInput = /** @type {HTMLInputElement|null} */ (document.getElementById('edit-image-id'));
  const id = idInput?.value || '';
  if (!id) return;

  const titleInput = /** @type {HTMLInputElement|null} */ (document.getElementById('edit-title'));
  const descriptionInput = /** @type {HTMLTextAreaElement|null} */ (document.getElementById('edit-description'));
  const catSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('edit-category'));
  const tagsInput = /** @type {HTMLInputElement|null} */ (document.getElementById('edit-tags'));
  const nsfwSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('edit-nsfw-select'));
  const cloneBtn = /** @type {HTMLButtonElement|null} */ (document.getElementById('edit-clone-btn'));

  const title = titleInput?.value.trim() || '';
  const description = descriptionInput?.value.trim() || '';
  const category = catSelect?.value || UNCATEGORIZED_CATEGORY;
  const tags = sanitizeTagList(tagsInput?.value || '');
  const nsfwRaw = nsfwSelect?.value;
  const is_nsfw = nsfwRaw === 'null' || nsfwRaw === undefined ? null : Number(nsfwRaw);

  if (cloneBtn) cloneBtn.disabled = true;
  try {
    const token = getAuthToken();
    /** @type {RequestInit} */
    let init;
    if (editSelectedFile) {
      const formData = new FormData();
      formData.append('file', editSelectedFile);
      formData.append('title', title);
      formData.append('description', description);
      formData.append('category', category);
      formData.append('tags', JSON.stringify(tags));
      formData.append('is_nsfw', nsfwRaw === undefined ? 'null' : nsfwRaw);
      const thumb = await createThumbnailFile(editSelectedFile);
      if (thumb) formData.append('thumbnail', thumb);
      init = {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token },
        body: formData
      };
    } else {
      init = {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ title, description, category, tags, is_nsfw })
      };
    }
    const res = await fetch(API_BASE + '/api/admin/images/' + encodeURIComponent(id) + '/clone', init);
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.image?.id) {
      showToast(data.error || 'Failed to clone image', 'error');
      return;
    }
    openEditModal(data.image);
    showToast('Cloned as an unpublished draft', 'success');
    void refreshImages();
  } catch {
    showToast('Network error cloning image', 'error');
  } finally {
    if (cloneBtn) cloneBtn.disabled = false;
  }
}

/**
 * Opens the edit modal for an admin image.
 * @param {AdminImage} img
 */
function openEditModal(img) {
  const editModal = document.getElementById('edit-modal');
  const editIdInput = /** @type {HTMLInputElement|null} */ (document.getElementById('edit-image-id'));
  const editTitleInput = /** @type {HTMLInputElement|null} */ (document.getElementById('edit-title'));
  const editCatSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('edit-category'));
  const editTagsInput = /** @type {HTMLInputElement|null} */ (document.getElementById('edit-tags'));
  const editActiveCheck = /** @type {HTMLInputElement|null} */ (document.getElementById('edit-is-active'));
  const editActiveToggleBtn = document.getElementById('edit-active-toggle-btn');
  const editNsfwSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('edit-nsfw-select'));

  if (editIdInput) editIdInput.value = img.id;
  if (editTitleInput) editTitleInput.value = img.title;
  const editDescriptionInput = /** @type {HTMLTextAreaElement|null} */ (document.getElementById('edit-description'));
  if (editDescriptionInput) editDescriptionInput.value = img.description || '';
  if (editCatSelect) editCatSelect.value = img.category;
  if (editTagsInput) editTagsInput.value = (img.tags || []).join(', ');
  const modalTitle = document.getElementById('image-modal-title');
  if (modalTitle) modalTitle.textContent = 'Edit Image';
  const dropHint = document.getElementById('edit-dropzone-hint');
  if (dropHint) dropHint.textContent = 'Drag & drop new image here or press Space to replace';
  document.getElementById('edit-dropzone')?.setAttribute(
    'aria-label',
    'Replace image. Press Space to browse, or drop a new image here.'
  );
  selectedImportUrl = null;

  const isActiveVal = img.is_active ? '1' : '0';
  if (editActiveCheck) editActiveCheck.value = isActiveVal;
  syncLobbyVisibilityToggle(editActiveToggleBtn, !!img.is_active);

  if (editNsfwSelect) {
    const nsfwVal = img.is_nsfw === null || img.is_nsfw === undefined ? 'null' : String(img.is_nsfw);
    editNsfwSelect.value = nsfwVal;
  }

  const editPreviewImg = /** @type {HTMLImageElement|null} */ (document.getElementById('edit-preview'));
  setDropzonePreview(editPreviewImg, img.image_url || '');
  const editAutodetectStatus = document.getElementById('edit-autodetect-status');
  if (editAutodetectStatus) editAutodetectStatus.textContent = '';
  const editGenerateStatus = document.getElementById('edit-generate-status');
  if (editGenerateStatus) {
    editGenerateStatus.style.display = 'none';
    editGenerateStatus.textContent = '';
  }
  editSelectedFile = null;
  initialEditActive = img.is_active ? 1 : 0;
  const editSubmitBtn = /** @type {HTMLButtonElement|null} */ (document.getElementById('submit-edit-btn'));
  if (editSubmitBtn) {
    editSubmitBtn.textContent = 'Save';
  }
  setCloneButtonVisible(true);

  editModal?.classList.add('open');
}

/**
 * Hides an admin image after confirm. The file stays so it can be restored.
 * @param {string} id
 */
async function deleteAdminImage(id) {
  if (!confirm('Delete this image? It stays hidden until you restore it from the Deleted filter.')) {
    return;
  }

  const token = getAuthToken();
  try {
    const res = await fetch(API_BASE + '/api/admin/images/' + id, {
      method: 'DELETE',
      headers: {
        'Authorization': 'Bearer ' + token
      }
    });
    if (res.ok) {
      showToast('Image deleted', 'success');
      refreshImages();
    } else {
      showToast('Failed to delete image', 'error');
    }
  } catch (err) {
    showToast('Network error deleting image', 'error');
  }
}

/**
 * Puts a soft-deleted image back into the catalog filters.
 * @param {string} id
 */
async function restoreAdminImage(id) {
  if (!confirm('Restore this image to the catalog?')) return;
  const token = getAuthToken();
  try {
    const res = await fetch(API_BASE + '/api/admin/images/' + encodeURIComponent(id) + '/restore', {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + token }
    });
    if (res.ok) {
      showToast('Image restored', 'success');
      refreshImages();
    } else {
      showToast('Failed to restore image', 'error');
    }
  } catch (err) {
    showToast('Network error restoring image', 'error');
  }
}

/**
 * PATCHes one catalog flag (publish or featured) and refreshes the Images tab.
 *
 * @param {string} id
 * @param {Record<string, boolean>} body
 * @param {string} okMessage
 * @param {string} failMessage
 * @returns {Promise<void>}
 */
async function patchAdminImage(id, body, okMessage, failMessage) {
  const token = getAuthToken();
  try {
    const res = await fetch(API_BASE + '/api/admin/images/' + id, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      },
      body: JSON.stringify(body)
    });
    if (res.ok) {
      showToast(okMessage, 'success');
      refreshImages();
    } else {
      showToast(failMessage, 'error');
    }
  } catch (err) {
    showToast('Network error updating image', 'error');
  }
}

function attachCardEvents() {
  document.querySelectorAll('#images-container .toggle-btn, #images-table-body .toggle-btn').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      const target = /** @type {HTMLElement} */ (e.currentTarget);
      const id = target.getAttribute('data-id');
      const currentActive = target.getAttribute('data-active') === '1';
      if (!id) return;
      await patchAdminImage(id, { is_active: !currentActive }, 'Image status updated', 'Failed to update status');
    });
  });

  document.querySelectorAll('#images-container .feature-btn, #images-table-body .feature-btn').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      const target = /** @type {HTMLElement} */ (e.currentTarget);
      const id = target.getAttribute('data-id');
      const currentFeatured = target.getAttribute('data-featured') === '1';
      if (!id) return;
      await patchAdminImage(
        id,
        { is_featured: !currentFeatured },
        currentFeatured ? 'Image unfeatured' : 'Image featured',
        'Failed to update featured state'
      );
    });
  });

  document.querySelectorAll('#images-container .edit-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const target = /** @type {HTMLElement} */ (e.currentTarget);
      const id = target.getAttribute('data-id');
      const img = allImages.find((item) => item.id === id);
      if (!img) return;
      openEditModal(img);
    });
  });

  document.querySelectorAll('#images-container .delete-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const target = /** @type {HTMLElement} */ (e.currentTarget);
      const id = target.getAttribute('data-id');
      if (id) deleteAdminImage(id);
    });
  });

  document.querySelectorAll('#images-container .restore-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const target = /** @type {HTMLElement} */ (e.currentTarget);
      const id = target.getAttribute('data-id');
      if (id) restoreAdminImage(id);
    });
  });
}

/**
 * Draws the generated photo up to 3200×1800 in steps of at most 2×, then
 * applies an unsharp mask so edges stay crisp after the enlarge.
 *
 * @param {CanvasImageSource} source
 * @param {number} sourceWidth
 * @param {number} sourceHeight
 * @returns {HTMLCanvasElement}
 */
function rasterizeGeneratedPhoto(source, sourceWidth, sourceHeight) {
  const steps = upscaleSteps(sourceWidth, sourceHeight);
  /** @type {CanvasImageSource} */
  let current = source;
  /** @type {HTMLCanvasElement} */
  let canvas = document.createElement('canvas');
  const draws = steps.length ? steps : [{ width: sourceWidth, height: sourceHeight }];
  for (const step of draws) {
    canvas = document.createElement('canvas');
    canvas.width = step.width;
    canvas.height = step.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not create canvas context');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(current, 0, 0, step.width, step.height);
    current = canvas;
  }
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not create canvas context');
  const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
  frame.data.set(unsharpMask(frame.data, canvas.width, canvas.height, GENERATED_SHARPEN_AMOUNT));
  ctx.putImageData(frame, 0, 0);
  return canvas;
}

/**
 * Shared helper for generating a stock photograph and upscaling it to 3200×1800.
 *
 * @param {Object} options
 * @param {string} options.title
 * @param {string} options.category
 * @param {string[]} options.tags
 * @param {HTMLInputElement|null} options.titleInput
 * @param {HTMLElement|null} options.generateBtn
 * @param {HTMLElement|null} options.generateStatus
 * @param {HTMLImageElement|null} options.previewImg
 * @param {(file: File) => void} options.onSuccess
 * @returns {Promise<void>}
 */
async function generateLandscapeImage({
  title,
  category,
  tags,
  titleInput,
  generateBtn,
  generateStatus,
  previewImg,
  onSuccess
}) {
  if (!title) {
    showToast('Please enter a Title or concept before generating an image', 'error');
    titleInput?.focus();
    return;
  }

  const sizeLabel = GENERATED_PUZZLE_WIDTH + '×' + GENERATED_PUZZLE_HEIGHT;
  if (generateBtn instanceof HTMLButtonElement) {
    generateBtn.disabled = true;
    generateBtn.innerHTML = `${SPARKLES_ICON_SVG} Generating photo...`;
  }
  if (generateStatus) {
    generateStatus.style.display = 'block';
    generateStatus.textContent = 'Generating a sharp stock photo and upscaling to ' + sizeLabel + ' JPG...';
  }

  try {
    const token = getAuthToken();
    const res = await fetch(API_BASE + '/api/admin/images/generate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      },
      body: JSON.stringify({ title, category, tags })
    });

    if (!res.ok) {
      throw new Error('Failed to generate image from server');
    }

    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);

    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = blobUrl;
    });

    const sourceWidth = img.naturalWidth || img.width || GENERATED_PUZZLE_WIDTH;
    const sourceHeight = img.naturalHeight || img.height || GENERATED_PUZZLE_HEIGHT;
    const canvas = rasterizeGeneratedPhoto(img, sourceWidth, sourceHeight);
    URL.revokeObjectURL(blobUrl);

    const jpegBlob = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', GENERATED_JPEG_QUALITY);
    });

    if (!jpegBlob) {
      throw new Error('Failed to encode ' + sizeLabel + ' JPEG');
    }

    const safeFilename = (title.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 32) || 'puzzle') + '_' + GENERATED_PUZZLE_WIDTH + 'x' + GENERATED_PUZZLE_HEIGHT + '.jpg';
    const generatedFile = new File([jpegBlob], safeFilename, { type: 'image/jpeg' });

    setDropzonePreview(previewImg, URL.createObjectURL(jpegBlob));

    showToast('Stock photo (' + sizeLabel + ') generated successfully!', 'success');
    onSuccess(generatedFile);
  } catch (err) {
    console.error('Image generation error:', err);
    showToast('Failed to generate image. Please try again.', 'error');
    if (generateStatus) {
      generateStatus.textContent = 'Generation failed. Please check network/key.';
    }
  } finally {
    if (generateBtn instanceof HTMLButtonElement) {
      generateBtn.disabled = false;
      generateBtn.innerHTML = `${SPARKLES_ICON_SVG} Generate`;
    }
  }
}

/**
 * Uses AI to suggest a creative title and description for a puzzle image in the selected category.
 * If no category is selected, randomly selects an available catalog category first.
 * @returns {Promise<void>}
 */
async function suggestRandomImageDetails() {
  const categorySelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('edit-category'));
  const titleInput = /** @type {HTMLInputElement|null} */ (document.getElementById('edit-title'));
  const descriptionInput = /** @type {HTMLTextAreaElement|null} */ (document.getElementById('edit-description'));
  const editDiceBtn = /** @type {HTMLButtonElement|null} */ (document.getElementById('edit-dice-prompt-btn'));
  const editAutodetectStatus = document.getElementById('edit-autodetect-status');

  const hasExistingText = Boolean(titleInput?.value?.trim() || descriptionInput?.value?.trim());
  if (hasExistingText && !confirm('Replace current title and description with new AI suggestions?')) {
    return;
  }

  // Check if a category is selected
  let currentCategory = categorySelect?.value?.trim() || '';
  const isUnselected = !currentCategory ||
    currentCategory === UNCATEGORIZED_CATEGORY ||
    currentCategory.toLowerCase() === 'all' ||
    Object.values(CATALOG_ACTIONS).includes(currentCategory);

  if (isUnselected && categorySelect) {
    const availableCategories = [...categorySelect.options]
      .map((opt) => opt.value?.trim())
      .filter((val) => Boolean(val) &&
        !Object.values(CATALOG_ACTIONS).includes(val) &&
        val.toLowerCase() !== 'all' &&
        val !== UNCATEGORIZED_CATEGORY);

    const pool = availableCategories.length > 0
      ? availableCategories
      : DEFAULT_CATEGORIES.filter((c) => c !== UNCATEGORIZED_CATEGORY);

    const randomCategory = pool[Math.floor(Math.random() * pool.length)];
    selectCategoryOption(categorySelect, randomCategory);
    categorySelect.dispatchEvent(new Event('change', { bubbles: true }));
    currentCategory = randomCategory;
  }

  const originalContent = editDiceBtn ? editDiceBtn.innerHTML : DICE_ICON_SVG;
  if (editDiceBtn) {
    editDiceBtn.disabled = true;
    editDiceBtn.innerHTML = HOURGLASS_ICON_SVG;
    editDiceBtn.setAttribute('aria-busy', 'true');
  }
  if (editAutodetectStatus) {
    editAutodetectStatus.textContent = `Thinking of a puzzle concept for ${currentCategory}…`;
  }

  try {
    const token = getAuthToken();
    const res = await fetch(API_BASE + '/api/admin/images/suggest', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token
      },
      body: JSON.stringify({ category: currentCategory })
    });

    if (!res.ok) {
      const errJson = await res.json().catch(() => ({}));
      throw new Error(errJson.error || `HTTP ${res.status}`);
    }

    const data = await res.json();
    if (!data.title && !data.description) {
      throw new Error('No suggestions returned');
    }

    if (titleInput && data.title) {
      titleInput.value = data.title;
      titleInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
    if (descriptionInput && data.description) {
      descriptionInput.value = data.description;
      descriptionInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    if (editAutodetectStatus) {
      editAutodetectStatus.textContent = `Suggested for ${data.category || currentCategory}: "${data.title}"`;
    }
    showToast(`Suggested: "${data.title}"`, 'success');
  } catch (err) {
    console.error('[Admin] suggestRandomImageDetails error:', err);
    const msg = err instanceof Error ? err.message : String(err);
    showToast('Failed to suggest image details: ' + msg, 'error');
    if (editAutodetectStatus) {
      editAutodetectStatus.textContent = 'Suggestion failed. Please try again.';
    }
  } finally {
    if (editDiceBtn) {
      editDiceBtn.disabled = false;
      editDiceBtn.innerHTML = originalContent;
      editDiceBtn.removeAttribute('aria-busy');
    }
  }
}

/**
 * Pulls the first http(s) URL out of dropped or pasted text.
 * @param {string} text
 * @returns {string|null}
 */
function extractImageUrl(text) {
  const raw = String(text || '').trim();
  if (!raw) return null;
  const match = raw.match(/https?:\/\/[^\s<>"']+/i);
  const candidate = match ? match[0] : raw;
  try {
    const u = new URL(candidate);
    if (u.protocol === 'http:' || u.protocol === 'https:') return u.toString();
  } catch {
    return null;
  }
  return null;
}

/**
 * Opens the shared image dialog with an empty id so submit creates a row.
 */
function openAddImageModal() {
  const title = document.getElementById('image-modal-title');
  if (title) title.textContent = 'Add Image';
  const form = document.getElementById('edit-form');
  if (form instanceof HTMLFormElement) form.reset();
  const idInput = /** @type {HTMLInputElement|null} */ (document.getElementById('edit-image-id'));
  if (idInput) idInput.value = '';
  editSelectedFile = null;
  selectedImportUrl = null;
  const preview = /** @type {HTMLImageElement|null} */ (document.getElementById('edit-preview'));
  setDropzonePreview(preview, '');
  const activeInput = /** @type {HTMLInputElement|null} */ (document.getElementById('edit-is-active'));
  if (activeInput) activeInput.value = '1';
  syncLobbyVisibilityToggle(document.getElementById('edit-active-toggle-btn'), true);
  const nsfwSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('edit-nsfw-select'));
  if (nsfwSelect) nsfwSelect.value = '0';
  const hint = document.getElementById('edit-dropzone-hint');
  if (hint) hint.textContent = 'Drag & drop image, paste a URL, or press Space to select';
  const dropzone = document.getElementById('edit-dropzone');
  dropzone?.setAttribute('aria-label', 'Select an image file. Press Space to browse, or drop or paste an image URL.');
  const status = document.getElementById('edit-autodetect-status');
  if (status) status.textContent = '';
  const generateStatus = document.getElementById('edit-generate-status');
  if (generateStatus) {
    generateStatus.style.display = 'none';
    generateStatus.textContent = '';
  }
  applyDefaultUploadCategory();
  const submitBtn = document.getElementById('submit-edit-btn');
  if (submitBtn) submitBtn.textContent = 'Upload';
  setCloneButtonVisible(false);
  document.getElementById('edit-modal')?.classList.add('open');
}

function initUploadHandlers() {
  const openUploadBtn = document.getElementById('open-upload-modal-btn');
  openUploadBtn?.addEventListener('click', () => openAddImageModal());
  initBulkUpload({
    apiBase: API_BASE,
    getToken: getAuthToken,
    onImported: () => { void fetchImages(); }
  });
}
function initEditHandlers() {
  const editModal = document.getElementById('edit-modal');
  const closeEditBtn = document.getElementById('close-edit-modal');
  const cancelEditBtn = document.getElementById('cancel-edit-btn');
  const editForm = document.getElementById('edit-form');
  const editDropzone = document.getElementById('edit-dropzone');
  const editFileInput = /** @type {HTMLInputElement|null} */ (document.getElementById('edit-file-input'));
  const editPreviewImg = /** @type {HTMLImageElement|null} */ (document.getElementById('edit-preview'));
  const editSubmitBtn = /** @type {HTMLButtonElement|null} */ (document.getElementById('submit-edit-btn'));

  const updateEditSubmitBtnText = () => {
    if (!editSubmitBtn) return;
    const idInput = /** @type {HTMLInputElement|null} */ (document.getElementById('edit-image-id'));
    if (!idInput?.value) {
      const isActive = activeInput?.value !== '0';
      editSubmitBtn.textContent = isActive ? 'Upload' : 'Save draft';
      return;
    }
    editSubmitBtn.textContent = editSubmitLabel({
      hasPendingFile: editSelectedFile !== null,
      currentActive: activeInput?.value === '1' ? 1 : 0,
      initialActive: initialEditActive
    });
  };

  const close = () => {
    editModal?.classList.remove('open');
    editSelectedFile = null;
    selectedImportUrl = null;
  };
  closeEditBtn?.addEventListener('click', close);
  cancelEditBtn?.addEventListener('click', close);

  editDropzone?.addEventListener('click', () => editFileInput?.click());

  editDropzone?.addEventListener('dragover', (e) => {
    e.preventDefault();
    editDropzone.classList.add('dragover');
  });

  editDropzone?.addEventListener('dragleave', () => {
    editDropzone.classList.remove('dragover');
  });

  editDropzone?.addEventListener('drop', (e) => {
    e.preventDefault();
    editDropzone.classList.remove('dragover');
    if (e.dataTransfer && e.dataTransfer.files.length > 0) {
      handleEditFileSelected(e.dataTransfer.files[0]);
      return;
    }
    const adding = !/** @type {HTMLInputElement|null} */ (document.getElementById('edit-image-id'))?.value;
    if (!adding) return;
    const uriList = e.dataTransfer?.getData('text/uri-list') || '';
    const plain = e.dataTransfer?.getData('text/plain') || '';
    const url = extractImageUrl(uriList) || extractImageUrl(plain);
    if (url) handleImportUrlSelected(url);
  });

  editDropzone?.addEventListener('paste', (e) => {
    const adding = !/** @type {HTMLInputElement|null} */ (document.getElementById('edit-image-id'))?.value;
    if (!adding) return;
    const text = e.clipboardData?.getData('text/plain') || '';
    const url = extractImageUrl(text);
    if (url) {
      e.preventDefault();
      handleImportUrlSelected(url);
    }
  });

  editModal?.addEventListener('paste', (e) => {
    if (!editModal.classList.contains('open')) return;
    const adding = !/** @type {HTMLInputElement|null} */ (document.getElementById('edit-image-id'))?.value;
    if (!adding) return;
    const text = e.clipboardData?.getData('text/plain') || '';
    const url = extractImageUrl(text);
    if (url) {
      e.preventDefault();
      handleImportUrlSelected(url);
    }
  });

  editFileInput?.addEventListener('change', () => {
    if (editFileInput.files && editFileInput.files.length > 0) {
      handleEditFileSelected(editFileInput.files[0]);
    }
  });

  /**
   * @param {string} url
   */
  function handleImportUrlSelected(url) {
    selectedImportUrl = url;
    editSelectedFile = null;
    if (editFileInput) editFileInput.value = '';
    const titleInput = /** @type {HTMLInputElement|null} */ (document.getElementById('edit-title'));
    if (titleInput && !titleInput.value) {
      try {
        const base = new URL(url).pathname.split('/').pop() || 'Imported Image';
        const nameWithoutExt = decodeURIComponent(base).replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
        titleInput.value = nameWithoutExt.charAt(0).toUpperCase() + nameWithoutExt.slice(1);
      } catch {
        titleInput.value = 'Imported Image';
      }
    }
    setDropzonePreview(editPreviewImg, url);
    updateEditSubmitBtnText();
  }

  function handleEditFileSelected(file) {
    editSelectedFile = file;
    selectedImportUrl = null;
    if (editPreviewImg) {
      readFileAsDataUrl(file)
        .then((dataUrl) => setDropzonePreview(editPreviewImg, dataUrl))
        .catch(() => setDropzonePreview(editPreviewImg, ''));
    }
    updateEditSubmitBtnText();
  }

  initDropzoneKeyboard(editDropzone, editFileInput);

  const editAutodetectBtn = document.getElementById('edit-autodetect-btn');
  const editAutodetectStatus = document.getElementById('edit-autodetect-status');
  editAutodetectBtn?.addEventListener('click', async () => {
    const idInput = /** @type {HTMLInputElement|null} */ (document.getElementById('edit-image-id'));
    await runImageAutoDetect({
      button: editAutodetectBtn,
      statusEl: editAutodetectStatus,
      file: editSelectedFile,
      imageUrl: selectedImportUrl,
      imageId: idInput?.value || '',
      titleInput: /** @type {HTMLInputElement|null} */ (document.getElementById('edit-title')),
      descriptionInput: /** @type {HTMLTextAreaElement|null} */ (document.getElementById('edit-description')),
      categorySelect: /** @type {HTMLSelectElement|null} */ (document.getElementById('edit-category')),
      tagsInput: /** @type {HTMLInputElement|null} */ (document.getElementById('edit-tags'))
    });
  });

  // "Generate Image" AI button handler for Edit modal
  const editGenerateBtn = document.getElementById('edit-generate-image-btn');
  const editGenerateStatus = document.getElementById('edit-generate-status');
  const editCloneBtn = document.getElementById('edit-clone-btn');
  editCloneBtn?.addEventListener('click', () => {
    void cloneEditingImage();
  });

  editGenerateBtn?.addEventListener('click', async () => {
    const titleInput = /** @type {HTMLInputElement|null} */ (document.getElementById('edit-title'));
    const categorySelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('edit-category'));
    const tagsInput = /** @type {HTMLInputElement|null} */ (document.getElementById('edit-tags'));

    const title = titleInput?.value.trim() || '';
    const category = categorySelect?.value || UNCATEGORIZED_CATEGORY;
    const tags = sanitizeTagList(tagsInput?.value || '');

    await generateLandscapeImage({
      title,
      category,
      tags,
      titleInput,
      generateBtn: editGenerateBtn,
      generateStatus: editGenerateStatus,
      previewImg: editPreviewImg,
      onSuccess: (file) => {
        editSelectedFile = file;
        selectedImportUrl = null;
        updateEditSubmitBtnText();
        if (editGenerateStatus) {
          const btnLabel = editSubmitBtn?.textContent || 'Save';
          editGenerateStatus.textContent = `Image ready at ${GENERATED_PUZZLE_WIDTH}×${GENERATED_PUZZLE_HEIGHT}px. Click "${btnLabel}" to save.`;
        }
      }
    });
  });

  const editDiceBtn = document.getElementById('edit-dice-prompt-btn');
  editDiceBtn?.addEventListener('click', async () => {
    await suggestRandomImageDetails();
  });

  const toggleBtn = document.getElementById('edit-active-toggle-btn');
  const activeInput = /** @type {HTMLInputElement|null} */ (document.getElementById('edit-is-active'));
  toggleBtn?.addEventListener('click', () => {
    const isCurrentlyActive = toggleBtn.getAttribute('data-active') === '1';
    const nextActive = !isCurrentlyActive;
    const nextVal = nextActive ? '1' : '0';
    syncLobbyVisibilityToggle(toggleBtn, nextActive);
    if (activeInput) activeInput.value = nextVal;
    updateEditSubmitBtnText();
  });

  editForm?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const idInput = /** @type {HTMLInputElement|null} */ (document.getElementById('edit-image-id'));
    const titleInput = /** @type {HTMLInputElement|null} */ (document.getElementById('edit-title'));
    const descriptionInput = /** @type {HTMLTextAreaElement|null} */ (document.getElementById('edit-description'));
    const catSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('edit-category'));
    const tagsInput = /** @type {HTMLInputElement|null} */ (document.getElementById('edit-tags'));
    const activeCheck = /** @type {HTMLInputElement|null} */ (document.getElementById('edit-is-active'));
    const nsfwSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('edit-nsfw-select'));

    const id = idInput?.value || '';

    const title = titleInput?.value.trim() || '';
    const description = descriptionInput?.value.trim() || '';
    const category = catSelect?.value || 'Other';
    const tags = sanitizeTagList(tagsInput?.value || '');
    if (tagsInput) tagsInput.value = tags.join(', ');
    const is_active = activeCheck ? (activeCheck.value === '1' ? 1 : 0) : 1;

    // is_nsfw: 'null' string → null, '0' → 0, '1' → 1
    const nsfwRaw = nsfwSelect?.value;
    const is_nsfw = nsfwRaw === 'null' || nsfwRaw === undefined ? null : Number(nsfwRaw);

    if (!id) {
      if (!editSelectedFile && !selectedImportUrl) {
        showToast('Please select an image file or paste an image URL', 'error');
        return;
      }
      if (editSubmitBtn) {
        editSubmitBtn.disabled = true;
        editSubmitBtn.textContent = selectedImportUrl ? 'Importing URL...' : 'Uploading...';
      }
      try {
        const token = getAuthToken();
        let res;
        if (selectedImportUrl) {
          res = await fetch(API_BASE + '/api/admin/images/url-import', {
            method: 'POST',
            headers: {
              'Authorization': 'Bearer ' + token,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              url: selectedImportUrl,
              title,
              description,
              category,
              tags,
              is_active,
              is_nsfw
            })
          });
        } else {
          const formData = new FormData();
          formData.append('file', /** @type {File} */(editSelectedFile));
          formData.append('title', title);
          formData.append('description', description);
          formData.append('category', category);
          formData.append('tags', JSON.stringify(tags));
          formData.append('is_active', String(is_active));
          formData.append('is_nsfw', nsfwRaw === undefined ? '0' : nsfwRaw);
          const thumb = await createThumbnailFile(/** @type {File} */(editSelectedFile));
          if (thumb) formData.append('thumbnail', thumb);
          res = await fetch(API_BASE + '/api/admin/images', {
            method: 'POST',
            headers: { 'Authorization': 'Bearer ' + token },
            body: formData
          });
        }
        if (res.ok) {
          showToast(
            is_active === 1
              ? 'Image uploaded and published successfully!'
              : 'Image uploaded successfully (unpublished)!',
            'success'
          );
          close();
          refreshImages();
        } else {
          const data = await res.json().catch(() => ({ error: 'Upload failed' }));
          showToast(data.error || 'Upload failed', 'error');
        }
      } catch {
        showToast('Error uploading image', 'error');
      } finally {
        if (editSubmitBtn) {
          editSubmitBtn.disabled = false;
          updateEditSubmitBtnText();
        }
      }
      return;
    }

    if (editSubmitBtn) {
      editSubmitBtn.disabled = true;
      editSubmitBtn.textContent = editSubmitPendingLabel(editSelectedFile !== null);
    }

    const token = getAuthToken();
    try {
      let res;
      if (editSelectedFile) {
        const formData = new FormData();
        formData.append('file', editSelectedFile);
        formData.append('title', title);
        formData.append('description', description);
        formData.append('category', category);
        formData.append('tags', JSON.stringify(tags));
        formData.append('is_active', String(is_active));
        formData.append('is_nsfw', String(is_nsfw));
        const thumb = await createThumbnailFile(editSelectedFile);
        if (thumb) formData.append('thumbnail', thumb);

        res = await fetch(API_BASE + '/api/admin/images/' + id, {
          method: 'PUT',
          headers: {
            'Authorization': 'Bearer ' + token
          },
          body: formData
        });
      } else {
        res = await fetch(API_BASE + '/api/admin/images/' + id, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token
          },
          body: JSON.stringify({
            title,
            description,
            category,
            tags,
            is_active,
            is_nsfw
          })
        });
      }

      if (res.ok) {
        showToast('Image updated successfully!', 'success');
        close();
        refreshImages();
      } else {
        showToast('Failed to update image', 'error');
      }
    } catch (err) {
      showToast('Network error updating image', 'error');
    } finally {
      if (editSubmitBtn) {
        editSubmitBtn.disabled = false;
        updateEditSubmitBtnText();
      }
    }
  });
}

/**
 * Seed Presets remains available via POST /api/admin/seed-presets for empty D1 /
 * new environments. The header button is intentionally omitted from the admin UI.
 */

/**
 * @typedef {Object} AdminRoom
 * @property {string} id
 * @property {string} [name]
 * @property {string} [image_url]
 * @property {string} [image_id]
 * @property {string} [creator_id]
 * @property {number} cols
 * @property {number} rows
 * @property {number} total_pieces
 * @property {number} active_sessions
 * @property {number} total_players
 * @property {number} is_completed
 * @property {number|null} [completed_time]
 * @property {number|null} [deleted_at]
 * @property {string|null} [deleted_reason]
 * @property {string|null} [guild_id]
 * @property {string|null} [channel_id]
 * @property {number} created_at
 * @property {number} last_activity_at
 */

/**
 * @typedef {Object} AdminUser
 * @property {string} id
 * @property {string|null} discord_id
 * @property {string} username
 * @property {string} display_name
 * @property {string|null} avatar
 * @property {string} discriminator
 * @property {number} is_discord_user
 * @property {number} is_active
 * @property {number} last_active_at
 * @property {number} created_at
 * @property {number} [pieces_solved]
 * @property {number} [playtime_seconds]
 * @property {number} [puzzles_created]
 * @property {number} [puzzles_completed]
 */

let activeTab = 'dashboard';
/** @type {AdminRoom[]} */
let allRooms = [];
/** @type {AdminUser[]} */
let allUsers = [];

/** @type {ReturnType<typeof setInterval>|null} */
let roomsPollTimer = null;

/**
 * Resets rooms pagination and fetches the first page.
 * @returns {Promise<void>}
 */
function refreshRooms() {
  roomsPage = 1;
  syncRoomsPolling();
  return fetchRooms();
}

/**
 * Resets users pagination and fetches the first page.
 * @returns {Promise<void>}
 */
function refreshUsers() {
  usersPage = 1;
  return fetchUsers();
}

/**
 * Resets flags pagination and fetches the first page.
 * @returns {Promise<void>}
 */
function refreshFlaggedMessages() {
  flagsPage = 1;
  return fetchFlaggedMessages();
}

/** @type {any[]} */
let cachedAdminChatMessages = [];

/**
 * Renders the admin chats table with optional filtering.
 */
function renderAdminChatsTable() {
  const body = document.getElementById('chats-table-body');
  if (!body) return;
  const search = (/** @type {HTMLInputElement|null} */ (document.getElementById('chats-search-filter'))?.value || '').trim().toLowerCase();

  let list = cachedAdminChatMessages;
  if (search) {
    list = list.filter((m) => {
      const text = String(m.text || '').toLowerCase();
      const sender = String(m.senderName || m.sender_name || '').toLowerCase();
      const room = String(m.roomId || m.room_id || '').toLowerCase();
      return text.includes(search) || sender.includes(search) || room.includes(search);
    });
  }

  if (!list.length) {
    const emptyMsg = search
      ? 'No messages matching "' + escapeAdminText(search) + '".'
      : 'No active room chat messages.';
    body.innerHTML = '<tr class="table-empty-row"><td colspan="5" data-label="" class="cell-empty">' + emptyMsg + '</td></tr>';
    return;
  }

  body.innerHTML = list.map((row) => {
    const when = formatAdminDate(row.timestamp || row.created_at);
    const text = escapeAdminText(row.text);
    const roomId = String(row.roomId || row.room_id || '').toUpperCase();
    const title = row.roomTitle ? String(row.roomTitle) : roomId;
    const isSystem = !!(row.isSystem || row.senderId === 'system');
    const isAdmin = !!row.isAdmin;
    const senderName = escapeAdminText(row.senderName || row.sender_name || (isSystem ? 'Puzzles' : 'Player'));

    let senderHtml = senderName;
    if (isSystem) {
      senderHtml = '<span class="badge badge-system">System</span> ' + senderName;
    } else if (isAdmin) {
      senderHtml = '<span class="badge badge-admin">Admin</span> ' + senderName;
    }

    const roomHtml = '<span class="cell-code" title="' + escapeAdminText(title) + '">' + escapeAdminText(roomId) + '</span>';
    const deleteBtn = '<button type="button" class="btn btn-danger btn-sm delete-chat-msg-btn" data-id="' + escapeAdminText(row.id) + '" data-room-id="' + escapeAdminText(roomId) + '" title="Delete this message">Delete</button>';

    return '<tr>' +
      tableCell('When', escapeAdminText(when), 'class="cell-date"') +
      tableCell('Room', roomHtml) +
      tableCell('Sender', senderHtml, 'class="cell-sender"') +
      tableCell('Message', '<span class="cell-value">' + text + '</span>', 'class="cell-message"') +
      tableCell('Actions', deleteBtn, 'class="cell-actions"') +
      '</tr>';
  }).join('');

  body.querySelectorAll('.delete-chat-msg-btn').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      const target = /** @type {HTMLElement} */ (e.currentTarget);
      const id = target.getAttribute('data-id');
      const msgRoomId = target.getAttribute('data-room-id');
      if (!id || !msgRoomId) return;
      if (!confirm('Delete message from room ' + msgRoomId + '?')) return;

      const token = getAuthToken();
      try {
        const query = '?roomId=' + encodeURIComponent(msgRoomId);
        const res = await fetch(API_BASE + '/api/admin/chat/messages/' + encodeURIComponent(id) + query, {
          method: 'DELETE',
          headers: { Authorization: 'Bearer ' + token }
        });
        if (res.ok) {
          showToast('Message deleted from room', 'success');
          await refreshAdminChats();
        } else {
          const errData = await res.json().catch(() => ({}));
          showToast(errData.error || 'Failed to delete message', 'error');
        }
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Network error deleting message', 'error');
      }
    });
  });
}

/**
 * Loads transcripts of all active room chats.
 * @returns {Promise<void>}
 */
async function refreshAdminChats() {
  const body = document.getElementById('chats-table-body');
  if (!body) return;
  const token = getAuthToken();
  try {
    const res = await fetch(API_BASE + '/api/admin/chats', {
      headers: { Authorization: 'Bearer ' + token }
    });
    if (res.status === 401) {
      showToast('Unauthorized: Check Admin Secret Key', 'error');
      return;
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      body.innerHTML = '<tr class="table-empty-row"><td colspan="5" data-label="" class="cell-empty">Could not load messages.</td></tr>';
      showToast(data.error || 'Could not load chats', 'error');
      return;
    }
    cachedAdminChatMessages = Array.isArray(data.messages) ? data.messages : [];
    renderAdminChatsTable();
  } catch (err) {
    body.innerHTML = '<tr class="table-empty-row"><td colspan="5" data-label="" class="cell-empty">Could not load messages.</td></tr>';
    showToast(err instanceof Error ? err.message : 'Could not load chats', 'error');
  }
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function escapeAdminText(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Compose modal for an announcement copied into every active room, and chat transcript controls. */
function initAdminChats() {
  const modal = document.getElementById('chats-compose-modal');
  const input = /** @type {HTMLTextAreaElement|null} */ (document.getElementById('chats-compose-input'));
  const open = () => {
    if (input) input.value = '';
    modal?.classList.add('open');
    input?.focus();
  };
  const close = () => modal?.classList.remove('open');
  document.getElementById('chats-compose-btn')?.addEventListener('click', open);
  document.getElementById('close-chats-compose-modal')?.addEventListener('click', close);
  document.getElementById('chats-compose-cancel')?.addEventListener('click', close);
  modal?.addEventListener('click', (event) => {
    if (event.target === modal) close();
  });
  document.getElementById('chats-refresh-btn')?.addEventListener('click', () => {
    refreshAdminChats();
  });
  document.getElementById('chats-search-filter')?.addEventListener('input', () => {
    renderAdminChatsTable();
  });
  document.getElementById('chats-compose-form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const text = input?.value.trim() || '';
    if (!text) return;
    const sendBtn = /** @type {HTMLButtonElement|null} */ (document.getElementById('chats-compose-send'));
    if (sendBtn) sendBtn.disabled = true;
    try {
      const res = await fetch(API_BASE + '/api/admin/chats', {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + getAuthToken(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(data.error || 'Could not send the message', 'error');
        return;
      }
      close();
      const result = adminBroadcastResult(data.roomCount, data.targeted, data.logged);
      showToast(result.text, result.tone);
      await refreshAdminChats();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not send the message', 'error');
    } finally {
      if (sendBtn) sendBtn.disabled = false;
    }
  });
}

/**
 * Switches the active tab view, updating URL history state and refreshing data.
 * @param {string} tabName
 * @param {{ pushState?: boolean, replaceState?: boolean, skipRefresh?: boolean, clearParams?: boolean, preserveParams?: boolean }} [options]
 */
function switchTab(tabName, options = {}) {
  const { pushState = true, replaceState = false, skipRefresh = false, clearParams = false, preserveParams = false } = options;
  const tab = /** @type {HTMLElement|null} */ (document.querySelector('.nav-tab-btn[data-tab="' + tabName + '"]'));
  if (!tab || tab.hidden || tab.getAttribute('hidden') != null) return;

  activeTab = tabName;
  const tabs = document.querySelectorAll('.nav-tab-btn');
  tabs.forEach((t) => t.classList.remove('active'));
  tab.classList.add('active');

  document.querySelectorAll('.tab-view').forEach((view) => {
    view.classList.remove('active');
  });

  const activeView = document.getElementById('tab-view-' + tabName);
  if (activeView) activeView.classList.add('active');

  syncNavCurrentLabel(document);

  // Update URL / History State
  try {
    const url = new URL(window.location.href);
    const currentUrlTab = new URLSearchParams(window.location.search).get('tab') || 'dashboard';
    const shouldClear = clearParams || (tabName === 'dashboard' && !preserveParams) || (currentUrlTab !== tabName && !preserveParams);

    if (shouldClear) {
      url.search = '';
      if (tabName !== 'dashboard') {
        url.searchParams.set('tab', tabName);
      }
      if (tabName === 'images') resetImageFilters();
      else if (tabName === 'users') resetUserFilters();
      else if (tabName === 'rooms') resetRoomFilters();
    } else {
      if (tabName === 'dashboard') {
        url.searchParams.delete('tab');
      } else {
        url.searchParams.set('tab', tabName);
      }
    }

    const currentSearch = window.location.search || '';
    const newSearch = url.search || '';
    const urlChanged = currentSearch !== newSearch;

    if (pushState) {
      if (currentUrlTab !== tabName || urlChanged) {
        window.history.pushState({ tab: tabName }, '', url.toString());
      }
    } else if (replaceState) {
      window.history.replaceState({ tab: tabName }, '', url.toString());
    }
  } catch {}

  // Refresh tab content
  if (!skipRefresh) {
    if (tabName === 'dashboard') {
      refreshDashboard();
    } else if (tabName === 'images') {
      refreshImages();
    } else if (tabName === 'rooms') {
      refreshRooms();
    } else if (tabName === 'users') {
      refreshUsers();
    } else if (tabName === 'catalog') {
      fetchCatalog();
    } else if (tabName === 'flagged') {
      refreshFlaggedMessages();
    } else if (tabName === 'bot') {
      refreshBotInstances();
    } else if (tabName === 'errors') {
      refreshClientErrors();
    } else if (tabName === 'messages') {
      refreshConversations();
    } else if (tabName === 'chats') {
      refreshAdminChats();
    } else if (tabName === 'settings') {
      loadSettings();
    }
  }

  // Polling belongs to the Bot, Rooms, and Dashboard tabs only; leaving them stops their timers.
  syncBotPolling();
  syncRoomsPolling();
  syncDashboardPolling(activeTab === 'dashboard');
}

/**
 * Resets all image filter controls to defaults before applying drill-down filter.
 */
function resetImageFilters() {
  const searchFilter = /** @type {HTMLInputElement|null} */ (document.getElementById('search-filter'));
  if (searchFilter) searchFilter.value = '';

  const catFilter = /** @type {HTMLSelectElement|null} */ (document.getElementById('category-filter'));
  if (catFilter) catFilter.value = 'All';

  const statusFilter = /** @type {HTMLSelectElement|null} */ (document.getElementById('image-status-filter'));
  if (statusFilter) statusFilter.value = 'all';

  const uploadedByFilter = /** @type {HTMLSelectElement|null} */ (document.getElementById('image-uploaded-by-filter'));
  if (uploadedByFilter) uploadedByFilter.value = 'all';

  const nsfwFilter = /** @type {HTMLSelectElement|null} */ (document.getElementById('image-nsfw-filter'));
  if (nsfwFilter) nsfwFilter.value = 'all';
}

/**
 * Resets all user filter controls to defaults before applying drill-down filter.
 */
function resetUserFilters() {
  const searchFilter = /** @type {HTMLInputElement|null} */ (document.getElementById('user-search-filter'));
  if (searchFilter) searchFilter.value = '';

  const statusFilter = /** @type {HTMLSelectElement|null} */ (document.getElementById('user-status-filter'));
  if (statusFilter) statusFilter.value = 'any';

  const typeFilter = /** @type {HTMLSelectElement|null} */ (document.getElementById('user-type-filter'));
  if (typeFilter) typeFilter.value = 'all';

  const sortSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('user-sort-select'));
  if (sortSelect) sortSelect.value = 'last_active';

  const orderSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('user-order-select'));
  if (orderSelect) orderSelect.value = 'desc';
}

/**
 * Resets all room filter controls to defaults before applying drill-down filter.
 */
function resetRoomFilters() {
  const searchFilter = /** @type {HTMLInputElement|null} */ (document.getElementById('room-search-filter'));
  if (searchFilter) searchFilter.value = '';

  const activitySelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('room-activity-filter'));
  if (activitySelect) activitySelect.value = 'any';

  const visibilitySelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('room-visibility-filter'));
  if (visibilitySelect) visibilitySelect.value = 'all';

  const sortSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('room-sort-select'));
  if (sortSelect) sortSelect.value = 'active_sessions';

  const orderSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('room-order-select'));
  if (orderSelect) orderSelect.value = 'desc';
}

/**
 * Applies URL query parameters as active filters for the given tab.
 * @param {string} tab
 */
function applyUrlFilters(tab) {
  try {
    const url = new URL(window.location.href);
    if (tab === 'images') {
      const imgId = url.searchParams.get('image_id') || url.searchParams.get('id');
      const category = url.searchParams.get('category');
      const tag = url.searchParams.get('tag');
      const q = url.searchParams.get('q');
      if (imgId || category || tag || q) {
        resetImageFilters();
        if (imgId) {
          const searchInput = /** @type {HTMLInputElement|null} */ (document.getElementById('search-filter'));
          if (searchInput) searchInput.value = imgId;
        } else if (category) {
          const catFilter = /** @type {HTMLSelectElement|null} */ (document.getElementById('category-filter'));
          if (catFilter) {
            if (![...catFilter.options].some((o) => o.value.toLowerCase() === category.toLowerCase())) {
              const opt = document.createElement('option');
              opt.value = category;
              opt.textContent = category;
              catFilter.appendChild(opt);
            }
            catFilter.value = category;
          }
        } else if (tag) {
          const searchInput = /** @type {HTMLInputElement|null} */ (document.getElementById('search-filter'));
          if (searchInput) searchInput.value = tag;
        } else if (q) {
          const searchInput = /** @type {HTMLInputElement|null} */ (document.getElementById('search-filter'));
          if (searchInput) searchInput.value = q;
        }
      }
    } else if (tab === 'users') {
      const status = url.searchParams.get('status');
      const q = url.searchParams.get('q');
      const type = url.searchParams.get('type') || url.searchParams.get('filter_type');
      if (status || q || type) {
        resetUserFilters();
        if (status) {
          const statusSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('user-status-filter'));
          if (statusSelect) statusSelect.value = status;
        }
        if (q) {
          const searchInput = /** @type {HTMLInputElement|null} */ (document.getElementById('user-search-filter'));
          if (searchInput) searchInput.value = q;
        }
        if (type) {
          const typeSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('user-type-filter'));
          if (typeSelect) typeSelect.value = type;
        }
      }
    } else if (tab === 'rooms') {
      const activity = url.searchParams.get('activity');
      const sort = url.searchParams.get('sort');
      const order = url.searchParams.get('order');
      const q = url.searchParams.get('q');
      if (activity || sort || order || q) {
        resetRoomFilters();
        if (activity) {
          const activitySelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('room-activity-filter'));
          if (activitySelect) activitySelect.value = activity;
        }
        if (sort) {
          const sortSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('room-sort-select'));
          if (sortSelect) sortSelect.value = sort;
        }
        if (order) {
          const orderSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('room-order-select'));
          if (orderSelect) orderSelect.value = order;
        }
        if (q) {
          const searchInput = /** @type {HTMLInputElement|null} */ (document.getElementById('room-search-filter'));
          if (searchInput) searchInput.value = q;
        }
      }
    }
  } catch {}
}

function initTabs() {
  const tabs = document.querySelectorAll('.nav-tab-btn');
  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const tabName = tab.getAttribute('data-tab');
      if (tabName) {
        switchTab(tabName, { pushState: true, clearParams: true });
      }
    });
  });

  // Clicking on the Puzzle icon in the top-left goes back to Dashboard
  const brandHome = document.getElementById('admin-brand-home');
  brandHome?.addEventListener('click', () => {
    switchTab('dashboard', { pushState: true });
  });
  brandHome?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      switchTab('dashboard', { pushState: true });
    }
  });

  // Browser back/forward navigation
  window.addEventListener('popstate', (e) => {
    const tabFromState = e.state?.tab;
    const tabFromUrl = new URLSearchParams(window.location.search).get('tab') || 'dashboard';
    const targetTab = tabFromState || tabFromUrl;
    if (targetTab) {
      applyUrlFilters(targetTab);
      if (targetTab !== activeTab) {
        switchTab(targetTab, { pushState: false, preserveParams: true });
      } else {
        refreshActiveTab();
      }
    }
  });

  document.getElementById('bot-autorefresh-check')?.addEventListener('change', syncBotPolling);
  document.getElementById('rooms-autorefresh-check')?.addEventListener('change', syncRoomsPolling);

  const requestedTab = new URLSearchParams(window.location.search).get('tab') || 'dashboard';
  applyUrlFilters(requestedTab);
  if (requestedTab && requestedTab !== 'dashboard') {
    switchTab(requestedTab, { pushState: false, replaceState: true, preserveParams: true });
  } else {
    try {
      window.history.replaceState({ tab: 'dashboard' }, '', window.location.href);
    } catch {}
  }

  initAdminNav();
}

/**
 * @typedef {Object} AdminBotInstance
 * @property {string} guild_id
 * @property {string} channel_id
 * @property {string|null} instance_id
 * @property {number|null} claimed_at
 * @property {number|null} last_seen_at
 * @property {number|null} disabled
 * @property {string|null} [room_id]
 * @property {string|null} [room_name]
 * @property {string|null} [image_url]
 * @property {string|null} [announce_channel_id]
 * @property {number|null} [cols]
 * @property {number|null} [rows]
 * @property {number|null} [active_sessions]
 * @property {number|null} [is_completed]
 * @property {number|null} [last_activity_at]
 */

/** A lease is considered live while it has been renewed inside its 120s TTL. */
const BOT_LEASE_TTL_MS = 120_000;

/**
 * Fetches one page of PuzzleBot instances.
 * @returns {Promise<void>}
 */
async function fetchBotInstances() {
  if (botLoading) return;
  botLoading = true;
  try {
    const params = new URLSearchParams({ limit: String(ADMIN_PAGE_LIMIT), page: String(botPage) });
    const res = await fetch(API_BASE + '/api/admin/bot/instances?' + params.toString(), {
      headers: { Authorization: 'Bearer ' + getAuthToken() }
    });
    if (res.status === 401) {
      showToast('Unauthorized: Check Admin Secret Key', 'error');
      return;
    }
    const data = await res.json();
    const page = data.instances || [];
    botPageMeta = metaFromResponse(data, botPage, page.length);
    botPage = botPageMeta.page;
    renderBotInstances(page);
    mountPagination('bot-pagination', botPageMeta, (next) => {
      botPage = next;
      fetchBotInstances();
    });
  } catch (err) {
    console.error('Failed to fetch bot instances:', err);
    showToast('Failed to load PuzzleBot instances', 'error');
  } finally {
    botLoading = false;
  }
}

/**
 * Renders the PuzzleBot instance table.
 * @param {AdminBotInstance[]} instances
 */
function renderBotInstances(instances) {
  const tbody = document.getElementById('bot-table-body');
  const countText = document.getElementById('bot-count-text');
  if (!tbody) return;

  if (countText) {
    const total = botPageMeta.total;
    countText.textContent = total + ' instance' + (total === 1 ? '' : 's');
  }

  if (instances.length === 0) {
    tbody.innerHTML = '<tr class="table-empty-row"><td colspan="14" data-label="" class="cell-empty">PuzzleBot is not connected to any channel.</td></tr>';
    return;
  }

  const now = Date.now();
  tbody.innerHTML = instances.map((row) => {
    const live = Number(row.last_seen_at || 0) > now - BOT_LEASE_TTL_MS;
    const status = row.disabled
      ? { label: 'Disabled', cls: 'status-inactive' }
      : row.room_id
        ? { label: 'Hosting', cls: 'status-active' }
        : live
          ? { label: 'Observing', cls: 'status-active' }
          : { label: 'Stale', cls: 'status-inactive' };
    const grid = row.cols && row.rows
      ? row.cols + ' &times; ' + row.rows
      : '<span class="muted">—</span>';
    const puzzle = row.room_id
      ? '<span class="mono-accent">' + escapeHtml(row.room_id) + '</span>' +
      (row.room_name ? ' <span class="subnote">' + escapeHtml(row.room_name) + '</span>' : '')
      : '<span class="muted">Idle</span>';
    const lastSeen = formatAdminDate(row.last_seen_at);
    const guildId = escapeHtml(row.guild_id);
    const thumb = row.image_url
      ? '<img src="' + escapeHtml(resolveAdminMediaUrl(row.image_url)) + '" alt="" class="table-thumb" loading="lazy" />'
      : '<span class="muted">—</span>';
    const completedLabel = row.is_completed
      ? '<span class="card-status-badge status-active badge-inline">Yes</span>'
      : '<span class="muted">—</span>';

    return (
      '<tr>' +
      tableCell('Guild', guildId, 'class="mono"') +
      tableCell('Channel', '<span class="mono">' + escapeHtml(row.channel_id) + '</span>') +
      tableCell('Announce', monoIdHtml(row.announce_channel_id)) +
      tableCell('Instance ID', monoIdHtml(row.instance_id)) +
      tableCell('Status', '<span class="card-status-badge ' + status.cls + ' badge-inline">' + status.label + '</span>') +
      tableCell('Puzzle', puzzle) +
      tableCell('Image', thumb) +
      tableCell('Grid', grid) +
      tableCell('Players', String(row.active_sessions || 0)) +
      tableCell('Completed', completedLabel) +
      tableCell('Claimed', formatAdminDate(row.claimed_at), 'class="cell-date"') +
      tableCell('Room activity', formatAdminDate(row.last_activity_at), 'class="cell-date"') +
      tableCell('Last seen', lastSeen, 'class="cell-date"') +
      tableCell('Actions',
        tableActionsHtml(
          '<button type="button" class="btn btn-secondary btn-sm bot-skip-btn" data-guild="' + guildId + '" title="Start the next puzzle now">Skip ' + SKIP_ICON_SVG + '</button>' +
          '<button type="button" class="btn btn-secondary btn-sm bot-release-btn" data-guild="' + guildId + '" title="Hand hosting back to the players">Release ' + HAND_ICON_SVG + '</button>' +
          (row.disabled
            ? '<button type="button" class="btn btn-secondary btn-sm bot-enable-btn" data-guild="' + guildId + '" title="Clear the operator block for this server">Enable</button>'
            : '<button type="button" class="btn btn-danger btn-sm bot-disable-btn" data-guild="' + guildId + '" title="Disable PuzzleBot for this server">Disable</button>')
        )) +
      '</tr>'
    );
  }).join('');

  wireBotRowActions(tbody);
}

/**
 * Wires the per-row control buttons.
 * @param {HTMLElement} tbody
 */
function wireBotRowActions(tbody) {
  /**
   * @param {string} selector
   * @param {(guildId: string) => Promise<void>} handler
   */
  const bind = (selector, handler) => {
    tbody.querySelectorAll(selector).forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        const guildId = /** @type {HTMLElement} */ (e.currentTarget).getAttribute('data-guild');
        if (guildId) await handler(guildId);
      });
    });
  };

  bind('.bot-skip-btn', (guildId) => botControlRequest(
    '/api/admin/bot/instances/' + encodeURIComponent(guildId) + '/skip',
    'POST',
    'Next puzzle started'
  ));
  bind('.bot-release-btn', (guildId) => botControlRequest(
    '/api/admin/bot/instances/' + encodeURIComponent(guildId) + '/release',
    'POST',
    'Hosting handed back'
  ));
  bind('.bot-enable-btn', (guildId) => botControlRequest(
    '/api/admin/bot/instances/' + encodeURIComponent(guildId) + '/enable',
    'POST',
    'PuzzleBot re-enabled for this server'
  ));
  bind('.bot-disable-btn', async (guildId) => {
    if (!confirm('Disable PuzzleBot for guild ' + guildId + '? Puzzles already running are left alone.')) return;
    await botControlRequest(
      '/api/admin/bot/instances/' + encodeURIComponent(guildId),
      'DELETE',
      'PuzzleBot disabled for this server'
    );
  });
}

/**
 * Sends one bot control request and refreshes the table.
 *
 * @param {string} path
 * @param {string} method
 * @param {string} successMessage
 * @returns {Promise<void>}
 */
async function botControlRequest(path, method, successMessage) {
  try {
    const res = await fetch(API_BASE + path, {
      method,
      headers: { Authorization: 'Bearer ' + getAuthToken() }
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Request failed');
    showToast(successMessage, 'success');
    fetchBotInstances();
  } catch (err) {
    showToast(err instanceof Error ? err.message : 'Request failed', 'error');
  }
}

/** Starts or stops the 5s poll, which only runs while the Rooms tab is the active one. */
function syncRoomsPolling() {
  const autoRefresh = /** @type {HTMLInputElement|null} */ (document.getElementById('rooms-autorefresh-check'));
  const shouldPoll = activeTab === 'rooms' && autoRefresh?.checked !== false;
  if (shouldPoll && !roomsPollTimer) {
    roomsPollTimer = setInterval(() => {
      if (document.visibilityState === 'visible') fetchRooms();
    }, 5000);
  } else if (!shouldPoll && roomsPollTimer) {
    clearInterval(roomsPollTimer);
    roomsPollTimer = null;
  }
}

/** Starts or stops the 5s poll, which only runs while the Bot tab is the active one. */
function syncBotPolling() {
  const autoRefresh = /** @type {HTMLInputElement|null} */ (document.getElementById('bot-autorefresh-check'));
  const shouldPoll = activeTab === 'bot' && autoRefresh?.checked !== false;
  if (shouldPoll && !botPollTimer) {
    botPollTimer = setInterval(() => {
      if (document.visibilityState === 'visible') fetchBotInstances();
    }, 5000);
  } else if (!shouldPoll && botPollTimer) {
    clearInterval(botPollTimer);
    botPollTimer = null;
  }
}

/**
 * Resets bot pagination, loads the first page, and (re)starts polling.
 * @returns {Promise<void>}
 */
function refreshBotInstances() {
  botPage = 1;
  syncBotPolling();
  return fetchBotInstances();
}

/** PuzzleBot on/off settings: form element id → `app_settings` key. */
const BOT_SWITCH_SETTINGS = {
  'setting-bot-enabled': 'bot_enabled',
  'setting-bot-announce': 'bot_announce_messages',
  'setting-bot-resume': 'bot_resume_last_puzzle',
  'setting-bot-allow-nsfw': 'bot_allow_nsfw'
};

/** PuzzleBot numeric settings: form element id → key plus the value shown when unset. */
const BOT_NUMBER_SETTINGS = {
  'setting-bot-next-delay': { key: 'bot_next_puzzle_delay_seconds', fallback: '30' },
  'setting-bot-host-idle': { key: 'bot_host_idle_seconds', fallback: '30' },
  'setting-bot-host-pick-window': { key: 'bot_host_pick_window_seconds', fallback: '15' },
  'setting-bot-max-channels': { key: 'bot_max_channels_per_guild', fallback: '1' },
  'setting-bot-max-idle': { key: 'bot_max_idle_puzzles', fallback: '2' },
  'setting-bot-lease-ttl': { key: 'bot_lease_ttl_seconds', fallback: '120' },
  'setting-bot-recent-image-memory': { key: 'bot_recent_image_memory', fallback: '10' },
  'setting-bot-candidate-limit': { key: 'bot_candidate_limit', fallback: '60' }
};

/**
 * Retention, room behaviour, chat, image intake, and support quotas.
 * Fallbacks are the real defaults, not a zero that the server would replace.
 */
const LIFECYCLE_NUMBER_SETTINGS = {
  'setting-public-rooms-query-limit': { key: 'public_rooms_query_limit', fallback: '40' },
  'setting-host-idle-seconds': { key: 'host_idle_seconds', fallback: '60' },
  'setting-host-disconnect-grace-seconds': { key: 'host_disconnect_grace_seconds', fallback: '10' },
  'setting-piece-hold-timeout-seconds': { key: 'piece_hold_timeout_seconds', fallback: '15' },
  'setting-max-multi-select-stack': { key: 'max_multi_select_stack', fallback: '10' },
  'setting-movement-smoothing-ms': { key: 'movement_smoothing_ms', fallback: '200' },
  'setting-room-history-days': { key: 'room_history_days', fallback: '14' },
  'setting-room-abandon-hours': { key: 'room_abandon_hours', fallback: '48' },
  'setting-room-soft-expire-days': { key: 'room_soft_expire_days', fallback: '14' },
  'setting-empty-room-expire-seconds': { key: 'empty_room_expire_seconds', fallback: '300' },
  'setting-room-hard-delete-days': { key: 'room_hard_delete_days', fallback: '60' },
  'setting-web-user-purge-days': { key: 'web_user_purge_days', fallback: '90' },
  'setting-discord-user-purge-days': { key: 'discord_user_purge_days', fallback: '365' },
  'setting-client-error-retention-days': { key: 'client_error_retention_days', fallback: '30' },
  'setting-chat-max-length': { key: 'chat_max_length', fallback: '500' },
  'setting-chat-rate-per-10s': { key: 'chat_rate_per_10s', fallback: '5' },
  'setting-chat-history-size': { key: 'chat_history_size', fallback: '100' },
  'setting-image-max-upload-mb': { key: 'image_max_upload_mb', fallback: '20' },
  'setting-image-min-width': { key: 'image_min_width', fallback: '1200' },
  'setting-image-min-height': { key: 'image_min_height', fallback: '800' },
  'setting-user-session-ttl-days': { key: 'user_session_ttl_days', fallback: '7' },
  'setting-admin-session-ttl-hours': { key: 'admin_session_ttl_hours', fallback: '12' }
};

/** Rate limiting on/off settings: form element id → `app_settings` key. */
const RATE_LIMIT_SWITCH_SETTINGS = {
  'setting-rate-limit-enabled': 'rate_limit_enabled'
};

/** Browser error collection on/off: form element id → `app_settings` key. */
const ERROR_REPORTING_SWITCH_SETTINGS = {
  'setting-error-reporting-enabled': 'error_reporting_enabled'
};

/** PuzzleBot player messages on/off: form element id → `app_settings` key. */
const SUPPORT_SWITCH_SETTINGS = {};

/** Feature flags: form element id → `app_settings` key. */
const FEATURE_FLAG_SWITCH_SETTINGS = {
  'setting-feature-lobby-nsfw-toggle': 'feature_lobby_nsfw_toggle',
  'setting-feature-unlisted-rooms': 'feature_unlisted_rooms',
  'setting-feature-room-passwords': 'feature_room_passwords',
  'setting-feature-creator-delete-public': 'feature_creator_delete_public_rooms',
  'setting-feature-activity-host-delete': 'feature_activity_host_delete_unlisted',
  'setting-feature-always-one-public-room': 'feature_always_one_web_public_room',
  'setting-feature-chat-enabled': 'feature_chat_enabled',
  'setting-feature-allow-public-spectators': 'feature_allow_public_spectators'
};

/** User flag and other message toggles: form element id → `app_settings` key. */
const FLAG_SWITCH_SETTINGS = {
  'setting-flag-image-enabled': 'flag_image_enabled',
  'setting-flag-message-enabled': 'flag_message_enabled',
  'setting-flag-user-enabled': 'flag_user_enabled',
  'setting-flag-suggestion-enabled': 'flag_suggestion_enabled',
  'setting-flag-feedback-enabled': 'flag_feedback_enabled'
};

/** Time-lapse replay and video export switches: form element id → `app_settings` key. */
const TIMELAPSE_SWITCH_SETTINGS = {
  'setting-enable-timelapse-replay': 'enable_timelapse_replay',
  'setting-enable-video-export': 'enable_video_export'
};

/** Rate limit tier budgets: form element id → key, tier, and the value shown when unset. */
const RATE_LIMIT_NUMBER_SETTINGS = {
  'setting-rate-limit-strict': { key: 'rate_limit_strict_per_10min', fallback: '0', tier: 'strict' },
  'setting-rate-limit-standard': { key: 'rate_limit_standard_per_10min', fallback: '0', tier: 'standard' }
};

/** Every registry-driven switch, as opposed to the bespoke fields above. */
const SWITCH_SETTINGS = {
  ...BOT_SWITCH_SETTINGS,
  ...RATE_LIMIT_SWITCH_SETTINGS,
  ...ERROR_REPORTING_SWITCH_SETTINGS,
  ...SUPPORT_SWITCH_SETTINGS,
  ...FEATURE_FLAG_SWITCH_SETTINGS,
  ...FLAG_SWITCH_SETTINGS,
  ...TIMELAPSE_SWITCH_SETTINGS
};

/** Every registry-driven numeric field. */
const NUMBER_SETTINGS = {
  ...BOT_NUMBER_SETTINGS,
  ...RATE_LIMIT_NUMBER_SETTINGS,
  ...LIFECYCLE_NUMBER_SETTINGS
};

/** Free-text integration fields. Empty means the API or that webhook is off. */
const TEXT_SETTINGS = {
  'setting-private-api-key': 'private_api_key',
  'setting-webhook-room-created': 'webhook_room_created_url',
  'setting-webhook-room-completed': 'webhook_room_completed_url',
  'setting-webhook-room-idle-expired': 'webhook_room_idle_expired_url',
  'setting-webhook-room-deleted': 'webhook_room_deleted_url',
  'setting-webhook-user-created': 'webhook_user_created_url',
  'setting-webhook-user-discord-linked': 'webhook_user_discord_linked_url',
  'setting-webhook-user-deleted': 'webhook_user_deleted_url',
  'setting-webhook-user-image-uploaded': 'webhook_user_image_uploaded_url',
  'setting-webhook-puzzlebot-activity': 'webhook_puzzlebot_activity_url',
  'setting-webhook-item-flagged': 'webhook_item_flagged_url',
  'setting-webhook-error-captured': 'webhook_error_captured_url',
  'setting-webhook-support-message-created': 'webhook_support_message_created_url',
  'setting-chat-room-start': 'chat_room_start_message',
  'setting-vision-model': 'vision_model',
  'setting-vision-fallback-model': 'vision_fallback_model',
  'setting-bot-default-grid-preset': 'bot_default_grid_preset',
  'setting-default-grid-preset': 'default_grid_preset',
  'setting-default-difficulty': 'default_difficulty',
  'setting-house-room-preset': 'house_room_preset',
  'setting-house-room-difficulty': 'house_room_difficulty',
  'setting-ai-prompt-style-suffix': 'ai_prompt_style_suffix'
};

/**
 * Shows each tier's burst ceiling in its hint, and warns when a budget sits above
 * what that ceiling can deliver. The ceiling lives in `wrangler.jsonc` and needs a
 * deploy to change, so without this an operator raising a budget sees no effect
 * and no explanation.
 */
function syncRateLimitCeilingHints() {
  for (const [id, entry] of Object.entries(RATE_LIMIT_NUMBER_SETTINGS)) {
    const input = /** @type {HTMLInputElement|null} */ (document.getElementById(id));
    const hint = document.getElementById(id + '-hint');
    if (!hint) continue;
    if (hint.dataset.baseHint === undefined) hint.dataset.baseHint = hint.textContent || '';
    const warning = tierCeilingWarning(entry.tier, input?.value);
    hint.textContent = [hint.dataset.baseHint, tierCeilingHint(entry.tier), warning]
      .filter(Boolean)
      .join(' ');
    hint.classList.toggle('field-hint-warn', Boolean(warning));
  }
}

/**
 * Matches the server's boolean parsing, so a stored `on` or `true` renders as checked.
 *
 * @param {any} value
 * @returns {boolean}
 */
function settingIsOn(value) {
  const v = String(value ?? '').trim().toLowerCase();
  return v === '1' || v === 'true' || v === 'yes' || v === 'on';
}

/**
 * Hides the Bot nav item when PuzzleBot hosting is off.
 * An open Bot page returns to Images.
 *
 * @param {Record<string, any>|null|undefined} settings
 */
function syncBotHostingNav(settings) {
  const visible = isDefaultOffNavVisible(settings?.bot_enabled);
  applyBotHostingNav(document, settings?.bot_enabled);
  if (!visible && activeTab === 'bot') {
    document.getElementById('tab-images-btn')?.click();
  }
}

/**
 * Hides the Messages nav item when players cannot message PuzzleBot.
 * An open Messages page returns to Images.
 *
 * @param {Record<string, any>|null|undefined} settings
 */
function syncSupportMessagesNav(settings) {
  const visible = isSupportMessagesNavVisible(settings?.support_messages_enabled)
    && (settings?.flag_suggestion_enabled !== '0' || settings?.flag_feedback_enabled !== '0');
  applySupportMessagesNav(document, visible ? '1' : '0');
  if (!visible && activeTab === 'messages') {
    document.getElementById('tab-images-btn')?.click();
  }
}

/**
 * Hides the Errors nav item when error collection is off.
 * An open Errors page returns to Images.
 *
 * @param {Record<string, any>|null|undefined} settings
 */
function syncErrorCollectionNav(settings) {
  const visible = isDefaultOnNavVisible(settings?.error_reporting_enabled);
  applyErrorCollectionNav(document, settings?.error_reporting_enabled);
  if (!visible && activeTab === 'errors') {
    document.getElementById('tab-images-btn')?.click();
  }
}

/**
 * Loads the nav gates before the operator opens Settings.
 * @returns {Promise<void>}
 */
async function loadGatedNav() {
  try {
    const res = await fetch(API_BASE + '/api/admin/settings', {
      headers: { Authorization: 'Bearer ' + getAuthToken() }
    });
    if (!res.ok) return;
    const data = await res.json();
    const settings = data.settings || {};
    syncBotHostingNav(settings);
    syncSupportMessagesNav(settings);
    syncErrorCollectionNav(settings);
  } catch {
    // Messages and Errors default on, so a failed read leaves those tabs in place.
    // PuzzleBot hosting defaults off; the Bot tab stays hidden until a read succeeds.
  }
}

/**
 * Populates the Settings form from a settings map.
 *
 * Shared by load and save so the form always shows what the server stored, not what was
 * typed — a value the server clamped (`room_hard_delete_days` below its 30-day floor)
 * would otherwise keep displaying the rejected number.
 *
 * @param {Record<string, any>} s
 */
/**
 * Keeps the field's own sentence and replaces a trailing bounds clause.
 * @param {HTMLElement|null} hint
 * @param {string} key
 */
function applyNumericFieldHint(hint, key) {
  if (!hint) return;
  const boundsText = numericSettingHint(key);
  if (!boundsText) return;
  const existing = hint.textContent || '';
  const cut = existing.search(/Minimum \d/);
  const prefix = (cut >= 0 ? existing.slice(0, cut) : existing)
    .replace(/\s*0 = default \(\d+\)\s*$/i, '')
    .replace(/\s*0 = built-in default\s*$/i, '')
    .replace(/\s*0 = no floor\s*$/i, '')
    .replace(/\s*0 = no ceiling\s*$/i, '')
    .trim();
  hint.textContent = prefix ? `${prefix} ${boundsText}` : boundsText;
}

function applySettingsToForm(s) {
  const webEnabled = /** @type {HTMLInputElement|null} */ (document.getElementById('setting-web-users-enabled'));
  const webMsg = /** @type {HTMLInputElement|null} */ (document.getElementById('setting-web-users-message'));
  const urlEnabled = /** @type {HTMLInputElement|null} */ (document.getElementById('setting-url-import-enabled'));
  const maxActive = /** @type {HTMLInputElement|null} */ (document.getElementById('setting-max-active-rooms'));
  const maxPerUser = /** @type {HTMLInputElement|null} */ (document.getElementById('setting-max-rooms-per-user'));
  const maxPlayers = /** @type {HTMLInputElement|null} */ (document.getElementById('setting-max-players-per-room'));

  if (webEnabled) webEnabled.checked = s.web_users_enabled !== '0';
  if (webMsg) webMsg.value = s.web_users_disabled_message || '';
  if (urlEnabled) urlEnabled.checked = s.user_url_import_enabled !== '0';
  if (maxActive) {
    maxActive.min = '1';
    maxActive.max = '10000';
    maxActive.value = String(clampNumericSetting('max_active_rooms', s.max_active_rooms ?? '1000'));
  }
  if (maxPerUser) {
    maxPerUser.min = '1';
    maxPerUser.max = '100';
    maxPerUser.value = String(clampNumericSetting('max_rooms_per_user', s.max_rooms_per_user ?? '10'));
  }
  if (maxPlayers) {
    maxPlayers.min = '1';
    maxPlayers.max = '500';
    maxPlayers.value = String(clampNumericSetting('max_players_per_room', s.max_players_per_room ?? '50'));
  }
  for (const input of [maxActive, maxPerUser, maxPlayers]) {
    const hintId = input?.getAttribute('aria-describedby');
    const hint = hintId ? document.getElementById(hintId) : null;
    const key = input?.id === 'setting-max-active-rooms'
      ? 'max_active_rooms'
      : input?.id === 'setting-max-rooms-per-user'
        ? 'max_rooms_per_user'
        : 'max_players_per_room';
    applyNumericFieldHint(hint, key);
  }

  for (const [id, key] of Object.entries(SWITCH_SETTINGS)) {
    const el = /** @type {HTMLInputElement|null} */ (document.getElementById(id));
    if (el) el.checked = settingIsOn(s[key]);
  }
  for (const [id, entry] of Object.entries(NUMBER_SETTINGS)) {
    const el = /** @type {HTMLInputElement|null} */ (document.getElementById(id));
    if (el) {
      const bounds = NUMERIC_SETTING_BOUNDS[entry.key];
      if (bounds) {
        el.min = String(bounds.min);
        el.max = String(bounds.max);
        el.value = String(clampNumericSetting(entry.key, s[entry.key] ?? entry.fallback));
        const hintId = el.getAttribute('aria-describedby');
        const hint = hintId ? document.getElementById(hintId) : null;
        applyNumericFieldHint(hint, entry.key);
      } else {
        el.value = String(s[entry.key] ?? entry.fallback);
      }
    }
  }
  for (const [id, key] of Object.entries(TEXT_SETTINGS)) {
    const el = /** @type {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement|null} */ (document.getElementById(id));
    if (el) el.value = resolveStoredGridPreset(id, s[key] ?? '');
  }
  syncRateLimitCeilingHints();
  syncBotHostingNav(s);
  syncSupportMessagesNav(s);
  syncErrorCollectionNav(s);
}

/**
 * Loads production settings into the Settings tab form.
 * @returns {Promise<void>}
 */
async function loadSettings() {
  const statusEl = document.getElementById('settings-status');
  if (statusEl) statusEl.textContent = 'Loading…';
  try {
    const token = getAuthToken();
    const res = await fetch(API_BASE + '/api/admin/settings', {
      headers: { Authorization: 'Bearer ' + token }
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to load settings');
    }
    const data = await res.json();
    applySettingsToForm(data.settings || {});
    // Success needs no message: the populated form is the confirmation.
    if (statusEl) statusEl.textContent = '';
    markSettingsSaved();
  } catch (err) {
    if (statusEl) statusEl.textContent = err instanceof Error ? err.message : 'Failed to load settings';
    showToast(err instanceof Error ? err.message : 'Failed to load settings', 'error');
  }
}

/**
 * Saves production settings from the Settings tab form.
 * @returns {Promise<void>}
 */
async function saveSettings() {
  const statusEl = document.getElementById('settings-status');
  const saveBtn = /** @type {HTMLButtonElement|null} */ (document.getElementById('save-settings-btn'));
  const webEnabled = /** @type {HTMLInputElement|null} */ (document.getElementById('setting-web-users-enabled'));
  const webMsg = /** @type {HTMLInputElement|null} */ (document.getElementById('setting-web-users-message'));
  const urlEnabled = /** @type {HTMLInputElement|null} */ (document.getElementById('setting-url-import-enabled'));
  const maxActive = /** @type {HTMLInputElement|null} */ (document.getElementById('setting-max-active-rooms'));
  const maxPerUser = /** @type {HTMLInputElement|null} */ (document.getElementById('setting-max-rooms-per-user'));
  const maxPlayers = /** @type {HTMLInputElement|null} */ (document.getElementById('setting-max-players-per-room'));

  const payload = {
    settings: {
      web_users_enabled: webEnabled?.checked ? '1' : '0',
      web_users_disabled_message: webMsg?.value.trim() || '',
      user_url_import_enabled: urlEnabled?.checked ? '1' : '0',
      max_active_rooms: String(clampNumericSetting('max_active_rooms', maxActive?.value ?? '1000')),
      max_rooms_per_user: String(clampNumericSetting('max_rooms_per_user', maxPerUser?.value ?? '10')),
      max_players_per_room: String(clampNumericSetting('max_players_per_room', maxPlayers?.value ?? '50'))
    }
  };

  for (const [id, key] of Object.entries(SWITCH_SETTINGS)) {
    const el = /** @type {HTMLInputElement|null} */ (document.getElementById(id));
    payload.settings[key] = el?.checked ? '1' : '0';
  }
  const suggestionEl = /** @type {HTMLInputElement|null} */ (document.getElementById('setting-flag-suggestion-enabled'));
  const feedbackEl = /** @type {HTMLInputElement|null} */ (document.getElementById('setting-flag-feedback-enabled'));
  if (suggestionEl || feedbackEl) {
    const anySupportEnabled = Boolean(suggestionEl?.checked || feedbackEl?.checked);
    payload.settings.support_messages_enabled = anySupportEnabled ? '1' : '0';
  }
  for (const [id, entry] of Object.entries(NUMBER_SETTINGS)) {
    const el = /** @type {HTMLInputElement|null} */ (document.getElementById(id));
    payload.settings[entry.key] = String(
      NUMERIC_SETTING_BOUNDS[entry.key]
        ? clampNumericSetting(entry.key, el?.value ?? entry.fallback)
        : Math.max(0, parseInt(el?.value || '0', 10) || 0)
    );
  }
  for (const [id, key] of Object.entries(TEXT_SETTINGS)) {
    const el = /** @type {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement|null} */ (document.getElementById(id));
    payload.settings[key] = el?.value.trim() || '';
  }

  if (saveBtn) saveBtn.disabled = true;
  if (statusEl) statusEl.textContent = 'Saving…';
  try {
    const token = getAuthToken();
    const res = await fetch(API_BASE + '/api/admin/settings', {
      method: 'PUT',
      headers: {
        Authorization: 'Bearer ' + token,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Save failed');
    if (data.settings) applySettingsToForm(data.settings);
    showToast('Settings saved', 'success');
    if (statusEl) statusEl.textContent = '';
    markSettingsSaved();
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Save failed';
    if (statusEl) statusEl.textContent = msg;
    showToast(msg, 'error');
    if (saveBtn) saveBtn.disabled = false;
  }
}

/**
 * Serializes the settings form so edits can be detected.
 * @returns {string}
 */
function serializeSettingsForm() {
  const form = document.getElementById('settings-form');
  if (!(form instanceof HTMLFormElement)) return '';
  const values = [...form.elements].map((el) => {
    if (el instanceof HTMLInputElement && el.type === 'checkbox') return el.id + '=' + (el.checked ? '1' : '0');
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return el.id + '=' + el.value;
    return '';
  });
  return values.join('&');
}

/** Marks the settings form as clean and disables Save until it changes again. */
function markSettingsSaved() {
  settingsSnapshot = serializeSettingsForm();
  const saveBtn = /** @type {HTMLButtonElement|null} */ (document.getElementById('save-settings-btn'));
  if (saveBtn) saveBtn.disabled = true;
}

/** Enables Save and flags unsaved edits whenever the settings form changes. */
function initSettingsDirtyTracking() {
  const form = document.getElementById('settings-form');
  const statusEl = document.getElementById('settings-status');
  const saveBtn = /** @type {HTMLButtonElement|null} */ (document.getElementById('save-settings-btn'));
  const sync = () => {
    syncRateLimitCeilingHints();
    const dirty = serializeSettingsForm() !== settingsSnapshot;
    if (saveBtn) saveBtn.disabled = !dirty;
    if (statusEl) statusEl.textContent = dirty ? 'Unsaved changes' : '';
  };
  form?.addEventListener('input', sync);
  form?.addEventListener('change', sync);
}

/**
 * Fetches one page of rooms via AJAX.
 */
async function fetchRooms() {
  if (roomsLoading) return;

  const token = getAuthToken();
  const searchInput = /** @type {HTMLInputElement|null} */ (document.getElementById('room-search-filter'));
  const activitySelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('room-activity-filter'));
  const visibilitySelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('room-visibility-filter'));
  const sortSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('room-sort-select'));
  const orderSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('room-order-select'));

  const q = searchInput?.value.trim() || '';
  const activity = activitySelect?.value || 'any';
  const visibility = visibilitySelect?.value || 'all';
  const sort = sortSelect?.value || 'active_sessions';
  const order = orderSelect?.value || 'desc';

  const params = new URLSearchParams({
    q,
    activity,
    visibility,
    sort,
    order,
    limit: String(ADMIN_PAGE_LIMIT),
    page: String(roomsPage)
  });

  roomsLoading = true;
  try {
    const res = await fetch(API_BASE + '/api/admin/rooms?' + params.toString(), {
      headers: {
        'Authorization': 'Bearer ' + token
      }
    });

    if (res.status === 401) {
      showToast('Unauthorized: Check Admin Secret Key', 'error');
      return;
    }

    const data = await res.json();
    const page = data.rooms || [];
    roomsPageMeta = metaFromResponse(data, roomsPage, page.length);
    roomsPage = roomsPageMeta.page;
    allRooms = page;
    renderRooms(allRooms);
    mountPagination('rooms-pagination', roomsPageMeta, (next) => {
      roomsPage = next;
      fetchRooms();
    });
  } catch (err) {
    console.error('Failed to fetch rooms:', err);
    showToast('Failed to load rooms from server', 'error');
  } finally {
    roomsLoading = false;
  }
}

/**
 * Renders room rows in the rooms table.
 * @param {AdminRoom[]} rooms
 */
/** @type {string} */
let publishRoomId = '';

/**
 * @param {string} roomId
 * @param {boolean} hasPassword
 */
function openPublishRoomDialog(roomId, hasPassword) {
  publishRoomId = roomId;
  const message = document.getElementById('publish-room-message');
  if (message) {
    message.textContent = `Room ${roomId}. ${roomPublishInstructions(hasPassword)}`;
  }
  document.getElementById('publish-room-modal')?.classList.add('open');
}

function closePublishRoomDialog() {
  publishRoomId = '';
  const confirmBtn = document.getElementById('publish-room-confirm');
  if (confirmBtn) confirmBtn.disabled = false;
  document.getElementById('publish-room-modal')?.classList.remove('open');
}

async function confirmPublishRoom() {
  const roomId = publishRoomId;
  if (!roomId) return;
  const confirmBtn = document.getElementById('publish-room-confirm');
  if (confirmBtn) confirmBtn.disabled = true;
  const auth = getAuthToken();
  try {
    const res = await fetch(API_BASE + '/api/admin/rooms/' + encodeURIComponent(roomId) + '/publish', {
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + auth }
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      closePublishRoomDialog();
      showToast('Room ' + roomId + ' is now public', 'success');
      refreshRooms();
      return;
    }
    showToast(data.error || 'Could not make the room public', 'error');
  } catch {
    showToast('Network error making the room public', 'error');
  }
  if (confirmBtn) confirmBtn.disabled = false;
}

function renderRooms(rooms) {
  const tbody = document.getElementById('rooms-table-body');
  const countText = document.getElementById('room-count-text');
  if (!tbody) return;
  if (countText) {
    const total = roomsPageMeta.total;
    countText.textContent = total + ' room' + (total === 1 ? '' : 's');
  }

  if (rooms.length === 0) {
    tbody.innerHTML = '<tr class="table-empty-row"><td colspan="16" data-label="" class="cell-empty">No puzzle rooms found matching filters.</td></tr>';
    return;
  }

  tbody.innerHTML = rooms.map((r) => {
    const status = adminRoomStatus(r);
    const createdDate = formatAdminDate(r.created_at);
    const lastActiveDate = formatAdminDate(r.last_activity_at);
    const activeCount = Number(r.active_sessions) || 0;
    const totalCount = Number(r.total_players) || 0;
    const activeBadgeClass = activeCount > 0 ? 'badge-active-sessions' : 'badge-web';
    const thumb = r.image_url
      ? '<img src="' + escapeHtml(resolveAdminMediaUrl(r.image_url)) + '" alt="" class="table-thumb" loading="lazy" />'
      : '<span class="muted">—</span>';
    const deletedLabel = r.deleted_at
      ? escapeHtml(String(r.deleted_reason || 'deleted')) + '<div class="subnote-tight">' + formatAdminDate(r.deleted_at) + '</div>'
      : '<span class="muted">—</span>';
    const diffLabel = difficultyLabel(r.difficulty);
    const diffHtml = diffLabel
      ? '<span class="badge badge-difficulty badge-difficulty-' + escapeHtml((r.difficulty || '').toLowerCase()) + '">' + escapeHtml(diffLabel) + '</span>'
      : '<span class="muted">—</span>';

    return (
      '<tr>' +
      tableCell('Room ID', '<span class="room-id-value">' + escapeHtml(r.id) + '</span>', 'class="cell-room-id"') +
      tableCell('Image', thumb) +
      tableCell('Dimensions', (r.cols || 20) + ' &times; ' + (r.rows || 10) + ' <span class="piece-count">(' + (r.total_pieces || 200) + ' pcs)</span>') +
      tableCell('Difficulty', diffHtml) +
      tableCell('Progress', progressDoughnutHtml(r, escapeHtml)) +
      tableCell(
        'Active/Total Players',
        '<span class="badge ' + activeBadgeClass + '" title="Connected sessions / players who have snapped or locked a piece">' +
        escapeHtml(formatActiveTotalPlayers(activeCount, totalCount)) +
        '</span>'
      ) +
      tableCell('Status', '<span class="card-status-badge ' + status.className + ' badge-inline">' + status.label + '</span>') +
      tableCell('Visibility', adminRoomVisibilityHtml(r, escapeHtml), 'class="cell-visibility"') +
      tableCell('Creator', monoIdHtml(r.creator_id)) +
      tableCell('Guild', monoIdHtml(r.guild_id)) +
      tableCell('Channel', monoIdHtml(r.channel_id)) +
      tableCell('Created', createdDate, 'class="cell-date"') +
      tableCell('Last Activity', lastActiveDate, 'class="cell-date"') +
      tableCell('Completed', formatAdminDate(adminRoomCompletedAt(r)), 'class="cell-date"') +
      tableCell('Deleted', deletedLabel, 'class="cell-danger"') +
      tableCell('Action',
        tableActionsHtml(
          '<a href="https://puzzles.samliew.net/room/' + encodeURIComponent(r.id) + '?silent=true" target="_blank" rel="noopener noreferrer" class="btn btn-secondary btn-sm btn-spectate" title="Join as silent spectator without alerting users">' +
          'Silent' +
          '</a>' +
          '<a href="https://puzzles.samliew.net/room/' + encodeURIComponent(r.id) + '" target="_blank" rel="noopener noreferrer" class="btn btn-secondary btn-sm">' +
          'Join' +
          '</a>' +
          '<button type="button" class="btn btn-danger btn-sm delete-room-btn" data-id="' + escapeHtml(r.id) + '">Delete</button>'
        )) +
      '</tr>'
    );
  }).join('');

  tbody.querySelectorAll('.room-visibility-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-room-id');
      if (!id) return;
      openPublishRoomDialog(id, btn.getAttribute('data-kind') === 'locked');
    });
  });

  tbody.querySelectorAll('.delete-room-btn').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      const id = /** @type {HTMLElement} */ (e.currentTarget).getAttribute('data-id');
      if (!id) return;
      if (!confirm('Delete room ' + id + '? This terminates the live session and removes the D1 row.')) return;

      const auth = getAuthToken();
      try {
        const res = await fetch(API_BASE + '/api/admin/rooms/' + encodeURIComponent(id), {
          method: 'DELETE',
          headers: { 'Authorization': 'Bearer ' + auth }
        });
        if (res.ok) {
          showToast('Room deleted', 'success');
          refreshRooms();
        } else {
          const data = await res.json().catch(() => ({}));
          showToast(data.error || 'Failed to delete room', 'error');
        }
      } catch (err) {
        showToast('Network error deleting room', 'error');
      }
    });
  });
}

/**
 * Fetches one page of users via AJAX.
 */
async function fetchUsers() {
  if (usersLoading) return;

  const token = getAuthToken();
  const searchInput = /** @type {HTMLInputElement|null} */ (document.getElementById('user-search-filter'));
  const statusFilterSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('user-status-filter'));
  const typeFilterSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('user-type-filter'));
  const sortSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('user-sort-select'));
  const orderSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('user-order-select'));

  const q = searchInput?.value.trim() || '';
  const statusFilter = statusFilterSelect?.value || 'any';
  const filterType = typeFilterSelect?.value || 'all';
  const sort = sortSelect?.value || 'last_active';
  const order = orderSelect?.value || 'desc';

  const params = new URLSearchParams({
    q,
    filter_type: filterType,
    sort,
    order,
    limit: String(ADMIN_PAGE_LIMIT),
    page: String(usersPage)
  });
  if (statusFilter === 'active') {
    params.set('filter_active', 'true');
  } else if (statusFilter === 'inactive') {
    params.set('filter_active', 'false');
  }

  usersLoading = true;
  try {
    const res = await fetch(API_BASE + '/api/admin/users?' + params.toString(), {
      headers: {
        'Authorization': 'Bearer ' + token
      }
    });

    if (res.status === 401) {
      showToast('Unauthorized: Check Admin Secret Key', 'error');
      return;
    }

    const data = await res.json();
    const page = data.users || [];
    usersPageMeta = metaFromResponse(data, usersPage, page.length);
    usersPage = usersPageMeta.page;
    allUsers = page;
    renderUsers(allUsers);
    mountPagination('users-pagination', usersPageMeta, (next) => {
      usersPage = next;
      fetchUsers();
    });
  } catch (err) {
    console.error('Failed to fetch users:', err);
    showToast('Failed to load users from server', 'error');
  } finally {
    usersLoading = false;
  }
}

/**
 * Formats playtime in seconds into a human-readable string (e.g. 2h 15m or 4m 30s).
 * @param {number} seconds
 * @returns {string}
 */
function formatPlaytime(seconds) {
  if (!seconds || seconds <= 0) return '0s';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hrs > 0) return `${hrs}h ${mins}m`;
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

/**
 * Renders user rows in the users table.
 * @param {AdminUser[]} users
 */
function renderUsers(users) {
  const tbody = document.getElementById('users-table-body');
  const countText = document.getElementById('user-count-text');
  if (!tbody) return;

  if (countText) {
    const total = usersPageMeta.total;
    countText.textContent = total + ' user' + (total === 1 ? '' : 's');
  }

  if (users.length === 0) {
    tbody.innerHTML = '<tr class="table-empty-row"><td colspan="12" data-label="" class="cell-empty">No users found matching filters.</td></tr>';
    return;
  }

  tbody.innerHTML = users.map((u) => {
    const typeBadge = u.is_discord_user
      ? '<span class="badge badge-discord">Discord</span>'
      : '<span class="badge badge-web">Web</span>';
    const statusClass = u.is_active ? 'status-active' : 'status-inactive';
    const statusLabel = u.is_active ? 'Active' : 'Offline';
    const avatarUrl = u.avatar || 'https://cdn.discordapp.com/embed/avatars/0.png';
    const lastActiveDate = formatAdminDate(u.last_active_at);
    const createdDate = formatAdminDate(u.created_at);
    const playtimeFormatted = formatPlaytime(u.playtime_seconds || 0);
    const piecesSolved = Number(u.pieces_solved || 0).toLocaleString();
    const puzzlesCreated = Number(u.puzzles_created || 0).toLocaleString();
    const puzzlesCompleted = Number(u.puzzles_completed || 0).toLocaleString();

    return (
      '<tr>' +
      tableCell('User',
        '<div class="user-row">' +
        '<img src="' + escapeHtml(avatarUrl) + '" alt="' + escapeHtml(u.username) + '" class="user-avatar" onerror="this.src=\'https://cdn.discordapp.com/embed/avatars/0.png\'" />' +
        '<div>' +
        '<div class="stack-title">' + escapeHtml(u.display_name || u.username) + '</div>' +
        '<div class="stack-sub">' + escapeHtml(u.id) + '</div>' +
        '</div>' +
        '</div>') +
      tableCell('User ID', monoIdHtml(u.id)) +
      tableCell('Discord ID', monoIdHtml(u.discord_id)) +
      tableCell('Type', typeBadge) +
      tableCell('Status', '<span class="card-status-badge ' + statusClass + ' badge-inline">' + statusLabel + '</span>') +
      tableCell('Pieces Solved', piecesSolved, 'class="cell-stat"') +
      tableCell('Playtime', playtimeFormatted, 'class="cell-quiet"') +
      tableCell('Created', puzzlesCreated, 'class="cell-quiet"') +
      tableCell('Completed', puzzlesCompleted, 'class="cell-quiet"') +
      tableCell('Last Active', lastActiveDate, 'class="cell-date"') +
      tableCell('Registered', createdDate, 'class="cell-date"') +
      tableCell('Action', tableActionsHtml('<button type="button" class="btn btn-danger btn-sm delete-user-btn" data-id="' + escapeHtml(u.id) + '">Delete</button>')) +
      '</tr>'
    );
  }).join('');

  tbody.querySelectorAll('.delete-user-btn').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      const id = /** @type {HTMLElement} */ (e.currentTarget).getAttribute('data-id');
      if (!id) return;
      if (!confirm('Delete this user permanently?')) return;

      const token = getAuthToken();
      try {
        const res = await fetch(API_BASE + '/api/admin/users/' + encodeURIComponent(id), {
          method: 'DELETE',
          headers: { 'Authorization': 'Bearer ' + token }
        });
        if (res.ok) {
          showToast('User deleted', 'success');
          refreshUsers();
        } else {
          const data = await res.json().catch(() => ({}));
          showToast(data.error || 'Failed to delete user', 'error');
        }
      } catch (err) {
        showToast('Network error deleting user', 'error');
      }
    });
  });
}

/**
 * @typedef {Object} ModerationFlag
 * @property {string} id
 * @property {string} entityType
 * @property {string} entityId
 * @property {string|null} [roomId]
 * @property {string} [reason]
 * @property {string} [details]
 * @property {string} [senderUsername]
 * @property {string} [senderId]
 * @property {number} reportCount
 * @property {string[]} reportedBy
 * @property {string} status
 * @property {number} createdAt
 * @property {number} updatedAt
 */

/** @type {ModerationFlag[]} */
let allFlaggedMessages = [];

/**
 * Creates a downscaled JPEG thumbnail File from an image File using canvas.
 *
 * @param {File} file
 * @param {number} [maxWidth=480]
 * @returns {Promise<File|null>}
 */
async function createThumbnailFile(file, maxWidth = 480) {
  if (typeof document === 'undefined' || !file || file.type === 'image/svg+xml') {
    return null;
  }
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxWidth / bitmap.width);
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.82));
    if (!blob) return null;
    return new File([blob], 'thumb.jpg', { type: 'image/jpeg' });
  } catch (err) {
    console.warn('Thumbnail generation failed:', err);
    return null;
  }
}

/**
 * @param {boolean} [append=false]
 */
async function fetchFlaggedMessages() {
  if (flagsLoading) return;

  const token = getAuthToken();
  const statusFilter = /** @type {HTMLSelectElement|null} */ (document.getElementById('flagged-status-filter'));
  const entityFilter = /** @type {HTMLSelectElement|null} */ (document.getElementById('flagged-entity-filter'));
  const status = statusFilter?.value || 'pending';
  const entityType = entityFilter?.value || 'all';

  flagsLoading = true;
  try {
    const params = new URLSearchParams({
      status,
      entity_type: entityType,
      limit: String(ADMIN_PAGE_LIMIT),
      page: String(flagsPage)
    });
    const res = await fetch(API_BASE + '/api/admin/flags?' + params.toString(), {
      headers: {
        'Authorization': 'Bearer ' + token
      }
    });

    if (res.status === 401) {
      showToast('Unauthorized: Check Admin Secret Key', 'error');
      return;
    }

    const data = await res.json();
    const page = data.flags || data.flaggedMessages || data.messages || [];
    flagsPageMeta = metaFromResponse(data, flagsPage, page.length);
    flagsPage = flagsPageMeta.page;
    allFlaggedMessages = page;
    renderFlaggedMessages(allFlaggedMessages);
    mountPagination('flagged-pagination', flagsPageMeta, (next) => {
      flagsPage = next;
      fetchFlaggedMessages();
    });
  } catch (err) {
    console.error('Failed to fetch flags:', err);
    showToast('Failed to load flagged content', 'error');
  } finally {
    flagsLoading = false;
  }
}

/**
 * Renders moderation flags in the Flags table.
 * @param {ModerationFlag[]} messages
 */
function renderFlaggedMessages(messages) {
  const tbody = document.getElementById('flagged-table-body');
  const countText = document.getElementById('flagged-count-text');
  if (!tbody) return;

  if (countText) {
    const total = flagsPageMeta.total;
    countText.textContent = total + ' report' + (total === 1 ? '' : 's');
  }

  if (messages.length === 0) {
    tbody.innerHTML = '<tr class="table-empty-row"><td colspan="12" data-label="" class="cell-empty">No flags found.</td></tr>';
    return;
  }

  tbody.innerHTML = messages.map((m) => {
    const statusClass = m.status === 'pending' ? 'badge-discord' : (m.status === 'deleted' || m.status === 'resolved' ? 'status-inactive' : 'status-active');
    const reportedDate = formatAdminDate(m.createdAt || m.created_at);
    const updatedDate = formatAdminDate(m.updatedAt || m.updated_at);
    const entityLabel = escapeHtml(m.entityType || 'message') + ': ' + escapeHtml(m.entityId || m.message_id || '');
    const roomLabel = m.roomId || m.room_id || '—';
    const details = m.details || m.message_text || '';
    const reason = m.reason || '';
    const sender = m.senderUsername || m.sender_username || m.senderId || m.sender_id || '';
    const reporters = Array.isArray(m.reportedBy)
      ? m.reportedBy.join(', ')
      : (m.reported_by || '');

    return (
      '<tr>' +
      tableCell('Type', '<span class="badge badge-discord">' + escapeHtml(m.entityType || 'message') + '</span>') +
      tableCell('Flag ID', monoIdHtml(m.id)) +
      tableCell('Entity / Room',
        '<div class="stack-title-soft">' + entityLabel + '</div>' +
        '<div class="stack-sub">Room: ' + escapeHtml(String(roomLabel)) + '</div>') +
      tableCell('Reason', escapeHtml(reason || '—'), 'class="cell-reason"') +
      tableCell('Details', escapeHtml(details || '—'), 'class="cell-details"') +
      tableCell('Debug', m.environment
        ? '<details class="flag-environment"><summary>View Details</summary><pre>' + escapeHtml(prettyEnvironment(m.environment)) + '</pre></details>'
        : '—') +
      tableCell('Sender', escapeHtml(sender || '—'), 'class="cell-date"') +
      tableCell('Reporter', escapeHtml(reporters || '—'), 'class="cell-reporter"') +
      tableCell('Reports', '<span class="badge report-count">' + (m.reportCount || m.report_count || 1) + '</span>') +
      tableCell('Status', '<span class="badge ' + statusClass + '">' + escapeHtml(m.status) + '</span>') +
      tableCell('Reported Date', reportedDate, 'class="cell-date"') +
      tableCell('Updated', updatedDate, 'class="cell-date"') +
      tableCell('Actions',
        tableActionsHtml(
          (m.status === 'pending' ? '<button class="btn btn-danger btn-sm resolve-flagged-btn" data-id="' + m.id + '">Resolve</button>' : '') +
          (m.status === 'pending' ? '<button class="btn btn-secondary btn-sm dismiss-flagged-btn" data-id="' + m.id + '">Dismiss</button>' : '') +
          ((m.entityType === 'message' || !m.entityType) && m.status !== 'deleted'
            ? '<button class="btn btn-danger btn-sm delete-flagged-btn" data-id="' + m.id + '">Delete Msg</button>'
            : '')
        )) +
      '</tr>'
    );
  }).join('');

  document.querySelectorAll('.delete-flagged-btn').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      const target = /** @type {HTMLElement} */ (e.currentTarget);
      const id = target.getAttribute('data-id');
      if (!id) return;
      if (!confirm('Delete this chat message?')) return;

      const token = getAuthToken();
      try {
        const res = await fetch(API_BASE + '/api/admin/chat/messages/' + encodeURIComponent(id), {
          method: 'DELETE',
          headers: { 'Authorization': 'Bearer ' + token }
        });
        if (res.ok) {
          showToast('Message deleted', 'success');
          refreshFlaggedMessages();
        } else {
          showToast('Failed to delete message', 'error');
        }
      } catch (err) {
        showToast('Network error deleting message', 'error');
      }
    });
  });

  document.querySelectorAll('.dismiss-flagged-btn').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      const target = /** @type {HTMLElement} */ (e.currentTarget);
      const id = target.getAttribute('data-id');
      if (!id) return;
      if (!confirm('Dismiss this moderation flag?')) return;
      const token = getAuthToken();
      try {
        const res = await fetch(API_BASE + '/api/admin/flags/' + encodeURIComponent(id) + '/dismiss', {
          method: 'POST',
          headers: { 'Authorization': 'Bearer ' + token }
        });
        if (res.ok) {
          showToast('Flag dismissed', 'success');
          refreshFlaggedMessages();
        } else {
          showToast('Failed to dismiss report', 'error');
        }
      } catch (err) {
        showToast('Network error dismissing report', 'error');
      }
    });
  });

  document.querySelectorAll('.resolve-flagged-btn').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      const target = /** @type {HTMLElement} */ (e.currentTarget);
      const id = target.getAttribute('data-id');
      if (!id) return;
      if (!confirm('Resolve this flag and apply the moderation action?')) return;
      const token = getAuthToken();
      try {
        const res = await fetch(API_BASE + '/api/admin/flags/' + encodeURIComponent(id) + '/resolve', {
          method: 'POST',
          headers: { 'Authorization': 'Bearer ' + token }
        });
        if (res.ok) {
          const data = await res.json();
          showToast('Resolved: ' + (data.action || 'ok'), 'success');
          refreshFlaggedMessages();
        } else {
          showToast('Failed to resolve flag', 'error');
        }
      } catch (err) {
        showToast('Network error resolving flag', 'error');
      }
    });
  });
}

/**
 * Reads the current Errors tab filter selections.
 *
 * @returns {{ source: string, status: string }}
 */
function errorFilters() {
  const sourceEl = /** @type {HTMLSelectElement|null} */ (document.getElementById('errors-source-filter'));
  const statusEl = /** @type {HTMLSelectElement|null} */ (document.getElementById('errors-status-filter'));
  return {
    source: sourceEl?.value || 'all',
    status: statusEl?.value || 'open'
  };
}

/**
 * Loads one page of collected browser errors.
 *
 * @returns {Promise<void>}
 */
async function fetchClientErrors() {
  if (errorsLoading) return;
  const token = getAuthToken();
  if (!token) return;

  errorsLoading = true;
  try {
    const { source, status } = errorFilters();
    const params = new URLSearchParams({
      source,
      status,
      limit: String(ADMIN_PAGE_LIMIT),
      page: String(errorsPage)
    });
    const res = await fetch(API_BASE + '/api/admin/errors?' + params.toString(), {
      headers: { 'Authorization': 'Bearer ' + token }
    });

    if (res.status === 401) {
      showToast('Unauthorized: Check Admin Secret Key', 'error');
      return;
    }

    const data = await res.json();
    const page = data.errors || [];
    errorsPageMeta = metaFromResponse(data, errorsPage, page.length);
    errorsPage = errorsPageMeta.page;
    renderClientErrors(page);
    mountPagination('errors-pagination', errorsPageMeta, (next) => {
      errorsPage = next;
      fetchClientErrors();
    });
  } catch (err) {
    console.error('Failed to fetch errors:', err);
    showToast('Failed to load error log', 'error');
  } finally {
    errorsLoading = false;
  }
}

/**
 * Resets to the first page and reloads the error log.
 *
 * @returns {void}
 */
function refreshClientErrors() {
  errorsPage = 1;
  fetchClientErrors();
}

/**
 * Renders the collected error rows.
 *
 * Every value here originates from an untrusted browser, so all of it is escaped.
 *
 * @param {Array<Record<string, any>>} errors
 * @returns {void}
 */
function renderClientErrors(errors) {
  const tbody = document.getElementById('errors-table-body');
  const countText = document.getElementById('errors-count-text');
  if (!tbody) return;

  if (countText) {
    const total = errorsPageMeta.total;
    countText.textContent = total + ' error' + (total === 1 ? '' : 's');
  }

  if (errors.length === 0) {
    tbody.innerHTML = '<tr class="table-empty-row"><td colspan="14" data-label="" class="cell-empty">No errors collected.</td></tr>';
    return;
  }

  tbody.innerHTML = errors.map((item) => {
    const where = item.pageUrl || '';
    const detail = item.stack || item.message || '';
    const resolved = item.status === 'resolved';
    return (
      '<tr>' +
      tableCell('Source', '<span class="badge badge-discord">' + escapeHtml(item.source || '') + '</span><br><span class="stack-sub">' + escapeHtml(item.kind || '') + '</span>') +
      tableCell(
        'Message',
        '<div class="message-text">' + escapeHtml(item.message || '') + '</div>' +
        '<details><summary class="stack-summary">Stack</summary>' +
        '<pre class="stack-pre">' + escapeHtml(detail) + '</pre></details>',
        'class="cell-error"'
      ) +
      tableCell('Where', '<span class="where-text">' + escapeHtml(where || '—') + '</span>') +
      tableCell('Room', monoIdHtml(item.roomId)) +
      tableCell('Country', escapeHtml(item.country || '—'), 'class="cell-date"') +
      tableCell('User ID', monoIdHtml(item.userId)) +
      tableCell('App version', escapeHtml(item.appVersion || '—'), 'class="cell-date"') +
      tableCell('Client IP', monoIdHtml(item.clientIp)) +
      tableCell('Fingerprint', monoIdHtml(truncateText(item.fingerprint, 24))) +
      tableCell('Count', String(Number(item.occurrenceCount) || 0)) +
      tableCell('Status', '<span class="badge">' + escapeHtml(item.status || '') + '</span>') +
      tableCell('First seen', formatAdminDate(item.firstSeenAt), 'class="cell-date"') +
      tableCell('Last Seen', formatAdminDate(item.lastSeenAt), 'class="cell-date"') +
      tableCell(
        'Actions',
        tableActionsHtml(
          '<button class="btn btn-secondary btn-sm error-status-btn" data-id="' + escapeHtml(item.id) + '" data-status="' + (resolved ? 'open' : 'resolved') + '">' +
          (resolved ? 'Reopen' : 'Resolve') + '</button>' +
          '<button class="btn btn-danger btn-sm error-delete-btn" data-id="' + escapeHtml(item.id) + '">Delete</button>'
        )
      ) +
      '</tr>'
    );
  }).join('');

  document.querySelectorAll('.error-status-btn').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      const target = /** @type {HTMLElement} */ (e.currentTarget);
      const id = target.getAttribute('data-id');
      const status = target.getAttribute('data-status') || 'resolved';
      if (!id) return;
      await sendErrorAction(
        '/api/admin/errors/' + encodeURIComponent(id) + '/status',
        { method: 'POST', body: JSON.stringify({ status }) },
        status === 'resolved' ? 'Error resolved' : 'Error reopened'
      );
    });
  });

  document.querySelectorAll('.error-delete-btn').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      const target = /** @type {HTMLElement} */ (e.currentTarget);
      const id = target.getAttribute('data-id');
      if (!id) return;
      if (!confirm('Delete this error entry?')) return;
      await sendErrorAction('/api/admin/errors/' + encodeURIComponent(id), { method: 'DELETE' }, 'Error deleted');
    });
  });
}

/**
 * Issues an authenticated error-log mutation and refreshes the list.
 *
 * @param {string} path
 * @param {RequestInit} init
 * @param {string} successMessage
 * @returns {Promise<void>}
 */
async function sendErrorAction(path, init, successMessage) {
  const token = getAuthToken();
  try {
    const res = await fetch(API_BASE + path, {
      ...init,
      headers: {
        'Authorization': 'Bearer ' + token,
        ...(init.body ? { 'Content-Type': 'application/json' } : {})
      }
    });
    if (res.ok) {
      showToast(successMessage, 'success');
      fetchClientErrors();
    } else {
      showToast('Action failed', 'error');
    }
  } catch (err) {
    showToast('Network error updating error log', 'error');
  }
}

/**
 * Wires the Errors tab toolbar.
 *
 * @returns {void}
 */
function initErrorsHandlers() {
  document.getElementById('errors-refresh-btn')?.addEventListener('click', refreshClientErrors);
  document.getElementById('errors-source-filter')?.addEventListener('change', refreshClientErrors);
  document.getElementById('errors-status-filter')?.addEventListener('change', refreshClientErrors);

  document.getElementById('errors-clear-btn')?.addEventListener('click', async () => {
    const { status } = errorFilters();
    const scope = status === 'all' ? 'every collected error' : 'all ' + status + ' errors';
    if (!confirm('Permanently delete ' + scope + '?')) return;
    const query = status === 'all' ? '' : '?status=' + encodeURIComponent(status);
    await sendErrorAction('/api/admin/errors/clear' + query, { method: 'POST' }, 'Error log cleared');
  });
}

/**
 * Reads the Messages tab filters.
 *
 * @returns {{ status: string, query: string }}
 */
function conversationFilters() {
  const statusEl = /** @type {HTMLSelectElement|null} */ (document.getElementById('messages-status-filter'));
  const searchEl = /** @type {HTMLInputElement|null} */ (document.getElementById('messages-search-filter'));
  return {
    status: statusEl?.value || 'all',
    query: (searchEl?.value || '').trim()
  };
}

/**
 * Loads one page of PuzzleBot conversations.
 *
 * @returns {Promise<void>}
 */
async function fetchConversations() {
  if (conversationsLoading) return;
  const token = getAuthToken();
  if (!token) return;

  conversationsLoading = true;
  try {
    const { status, query } = conversationFilters();
    const params = new URLSearchParams({
      status,
      limit: String(ADMIN_PAGE_LIMIT),
      page: String(conversationsPage)
    });
    if (query) params.set('query', query);

    const res = await fetch(API_BASE + '/api/admin/support/conversations?' + params.toString(), {
      headers: { 'Authorization': 'Bearer ' + token }
    });

    if (res.status === 401) {
      showToast('Unauthorized: Check Admin Secret Key', 'error');
      return;
    }

    const data = await res.json();
    const page = data.conversations || [];
    conversationsPageMeta = metaFromResponse(data, conversationsPage, page.length);
    conversationsPage = conversationsPageMeta.page;
    renderConversations(page, Number(data.unreadTotal) || 0);
    mountPagination('messages-pagination', conversationsPageMeta, (next) => {
      conversationsPage = next;
      fetchConversations();
    });
  } catch (err) {
    console.error('Failed to fetch conversations:', err);
    showToast('Failed to load conversations', 'error');
  } finally {
    conversationsLoading = false;
  }
}

/**
 * Resets to the first page and reloads conversations.
 *
 * @returns {void}
 */
function refreshConversations() {
  conversationsPage = 1;
  fetchConversations();
}

/**
 * Renders the conversation rows.
 *
 * Display names and previews are player-authored, so every value is escaped.
 *
 * @param {Array<Record<string, any>>} conversations
 * @param {number} unreadTotal
 * @returns {void}
 */
function renderConversations(conversations, unreadTotal) {
  const tbody = document.getElementById('messages-table-body');
  const countText = document.getElementById('messages-count-text');
  if (!tbody) return;

  if (countText) {
    const total = conversationsPageMeta.total;
    countText.textContent = total + ' conversation' + (total === 1 ? '' : 's')
      + (unreadTotal > 0 ? ' · ' + unreadTotal + ' awaiting reply' : '');
  }

  if (conversations.length === 0) {
    tbody.innerHTML = '<tr class="table-empty-row"><td colspan="9" data-label="" class="cell-empty">No conversations yet.</td></tr>';
    return;
  }

  tbody.innerHTML = conversations.map((item) => {
    const needsReply = Number(item.unreadAdmin) > 0;
    const typeBadge = item.isDiscordUser
      ? '<span class="badge badge-discord">Discord</span>'
      : '<span class="badge">Web</span>';
    const preview = item.lastSender === 'bot'
      ? 'PuzzleBot: ' + (item.lastPreview || '')
      : (item.lastPreview || '');
    return (
      '<tr>' +
      tableCell(
        'Player',
        '<div class="message-text">' + escapeHtml(item.displayName || '(no name)') + '</div>' +
        '<div class="id-break">' + escapeHtml(item.userId || '') + '</div>' +
        typeBadge
      ) +
      tableCell(
        'Latest message',
        '<span class="preview-text">' + escapeHtml(preview) + '</span>',
        'class="cell-preview"'
      ) +
      tableCell('Messages', String(Number(item.messageCount) || 0)) +
      tableCell('Unread (player)', String(Number(item.unreadUser) || 0), 'class="cell-meta"') +
      tableCell(
        'Status',
        '<span class="badge">' + escapeHtml(item.status || 'open') + '</span>' +
        (needsReply ? ' <span class="badge badge-nsfw">Needs reply</span>' : '')
      ) +
      tableCell('Created', formatAdminDate(item.createdAt), 'class="cell-date"') +
      tableCell('Updated', formatAdminDate(item.updatedAt), 'class="cell-date"') +
      tableCell('Last Activity', formatAdminDate(item.lastMessageAt), 'class="cell-date"') +
      tableCell(
        'Actions',
        tableActionsHtml(
          '<button class="btn btn-primary btn-sm conversation-open-btn" data-user-id="' + escapeHtml(item.userId) + '">Open</button>'
        )
      ) +
      '</tr>'
    );
  }).join('');

  document.querySelectorAll('.conversation-open-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const userId = /** @type {HTMLElement} */ (e.currentTarget).getAttribute('data-user-id');
      if (userId) openConversation(userId);
    });
  });
}

/**
 * Loads one conversation into the modal. Opening it is what marks it read.
 *
 * @param {string} userId
 * @returns {Promise<void>}
 */
async function openConversation(userId) {
  const token = getAuthToken();
  if (!token) return;

  try {
    const res = await fetch(API_BASE + '/api/admin/support/conversations/' + encodeURIComponent(userId), {
      headers: { 'Authorization': 'Bearer ' + token }
    });
    if (!res.ok) {
      showToast('Could not open conversation', 'error');
      return;
    }
    const data = await res.json();
    activeConversationUserId = userId;
    renderConversationThread(data.conversation || {}, data.messages || []);
    document.getElementById('conversation-modal')?.classList.add('open');
  } catch (err) {
    showToast('Network error opening conversation', 'error');
  }
}

/**
 * Draws the open conversation and its attached environment snapshots.
 *
 * @param {Record<string, any>} conversation
 * @param {Array<Record<string, any>>} messages
 * @returns {void}
 */
function renderConversationThread(conversation, messages) {
  const title = document.getElementById('conversation-title');
  const subtitle = document.getElementById('conversation-subtitle');
  const thread = document.getElementById('conversation-thread');
  const statusBtn = document.getElementById('conversation-status-btn');

  if (title) title.textContent = conversation.displayName || conversation.userId || 'Conversation';
  if (subtitle) {
    subtitle.textContent = [
      conversation.userId,
      conversation.isDiscordUser ? 'Discord account' : 'Web account',
      (conversation.status || 'open') + ' · ' + (Number(conversation.messageCount) || 0) + ' messages'
    ].filter(Boolean).join(' · ');
  }
  if (statusBtn) {
    const closing = (conversation.status || 'open') !== 'closed';
    statusBtn.textContent = closing ? 'Close conversation' : 'Reopen conversation';
    statusBtn.setAttribute('data-next-status', closing ? 'closed' : 'open');
  }
  if (!thread) return;

  if (!messages.length) {
    thread.innerHTML = '<div class="empty-note">No messages.</div>';
    return;
  }

  thread.innerHTML = messages.map((msg) => {
    const isBot = msg.sender === 'bot';
    const when = msg.createdAt ? new Date(Number(msg.createdAt)).toLocaleString() : '';
    const environment = msg.environment
      ? '<details class="conversation-environment"><summary>Environment details</summary><pre>'
      + escapeHtml(prettyEnvironment(msg.environment)) + '</pre></details>'
      : '';
    return (
      '<div class="conversation-message' + (isBot ? ' conversation-message-bot' : '') + '">' +
      '<div class="conversation-message-meta">' + escapeHtml(isBot ? 'PuzzleBot' : (msg.authorName || 'Player'))
      + (when ? ' · ' + escapeHtml(when) : '') + '</div>' +
      '<div class="conversation-message-body">' + escapeHtml(msg.body || '') + '</div>' +
      environment +
      '</div>'
    );
  }).join('');

  thread.scrollTop = thread.scrollHeight;
}

/**
 * Re-indents a stored environment snapshot for reading. Stored JSON is compact, and
 * an unparseable value is shown verbatim rather than hidden.
 *
 * @param {string} raw
 * @returns {string}
 */
function prettyEnvironment(raw) {
  try {
    return JSON.stringify(JSON.parse(String(raw)), null, 2);
  } catch {
    return String(raw || '');
  }
}

/**
 * Closes the conversation modal.
 *
 * @returns {void}
 */
function closeConversationModal() {
  document.getElementById('conversation-modal')?.classList.remove('open');
  activeConversationUserId = '';
  const input = /** @type {HTMLTextAreaElement|null} */ (document.getElementById('conversation-reply-input'));
  if (input) input.value = '';
}

/**
 * Issues an authenticated conversation mutation.
 *
 * @param {string} path
 * @param {RequestInit} init
 * @param {string} successMessage
 * @returns {Promise<boolean>}
 */
async function sendConversationAction(path, init, successMessage) {
  const token = getAuthToken();
  try {
    const res = await fetch(API_BASE + path, {
      ...init,
      headers: {
        'Authorization': 'Bearer ' + token,
        ...(init.body ? { 'Content-Type': 'application/json' } : {})
      }
    });
    if (!res.ok) {
      showToast('Action failed', 'error');
      return false;
    }
    showToast(successMessage, 'success');
    fetchConversations();
    return true;
  } catch (err) {
    showToast('Network error updating conversation', 'error');
    return false;
  }
}

/**
 * Wires the Messages tab toolbar and the conversation modal.
 *
 * @returns {void}
 */
function initConversationHandlers() {
  document.getElementById('messages-refresh-btn')?.addEventListener('click', refreshConversations);
  document.getElementById('close-conversation-modal')?.addEventListener('click', closeConversationModal);

  document.getElementById('conversation-reply-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const input = /** @type {HTMLTextAreaElement|null} */ (document.getElementById('conversation-reply-input'));
    const body = (input?.value || '').trim();
    if (!activeConversationUserId || !body) return;
    const sent = await sendConversationAction(
      '/api/admin/support/conversations/' + encodeURIComponent(activeConversationUserId) + '/reply',
      { method: 'POST', body: JSON.stringify({ body }) },
      'Reply sent as PuzzleBot'
    );
    if (sent) {
      if (input) input.value = '';
      await openConversation(activeConversationUserId);
    }
  });

  document.getElementById('conversation-status-btn')?.addEventListener('click', async (e) => {
    const status = /** @type {HTMLElement} */ (e.currentTarget).getAttribute('data-next-status') || 'closed';
    if (!activeConversationUserId) return;
    const changed = await sendConversationAction(
      '/api/admin/support/conversations/' + encodeURIComponent(activeConversationUserId) + '/status',
      { method: 'POST', body: JSON.stringify({ status }) },
      status === 'closed' ? 'Conversation closed' : 'Conversation reopened'
    );
    if (changed) await openConversation(activeConversationUserId);
  });

  document.getElementById('conversation-delete-btn')?.addEventListener('click', async () => {
    if (!activeConversationUserId) return;
    if (!confirm('Permanently delete this conversation and every message in it?')) return;
    const deleted = await sendConversationAction(
      '/api/admin/support/conversations/' + encodeURIComponent(activeConversationUserId),
      { method: 'DELETE' },
      'Conversation deleted'
    );
    if (deleted) closeConversationModal();
  });
}

/**
 * Populates title, category, and tags from the selected image.
 *
 * Prefers a pending upload/replacement file, then a pasted import URL, then the
 * stored image being edited. Existing catalog values are sent as hints so the
 * API reuses them instead of inventing new ones.
 *
 * @param {Object} options
 * @param {HTMLElement|null} options.button
 * @param {HTMLElement|null} options.statusEl
 * @param {File|null} [options.file]
 * @param {string} [options.imageId]
 * @param {string|null} [options.imageUrl]
 * @param {HTMLInputElement|null} options.titleInput
 * @param {HTMLInputElement|HTMLTextAreaElement|null} [options.descriptionInput]
 * @param {HTMLSelectElement|null} options.categorySelect
 * @param {HTMLInputElement|null} options.tagsInput
 * @returns {Promise<void>}
 */
async function runImageAutoDetect({
  button,
  statusEl,
  file = null,
  imageId = '',
  imageUrl = null,
  titleInput,
  /** @type {HTMLInputElement|HTMLTextAreaElement|null} */
  descriptionInput = null,
  categorySelect,
  tagsInput
}) {
  /** @type {Record<string, unknown>} */
  const payload = {
    categories: catalogCategories.map((c) => c.name),
    tags: catalogTags.map((t) => t.name)
  };

  if (file) {
    try {
      payload.dataUrl = await readFileAsDataUrl(file);
    } catch {
      showToast('Could not read the selected image', 'error');
      return;
    }
  } else if (imageUrl) {
    payload.imageUrl = imageUrl;
  } else if (imageId) {
    payload.imageId = imageId;
  } else {
    if (statusEl) statusEl.textContent = 'Select an image first.';
    showToast('Select an image before auto-detecting', 'error');
    return;
  }

  const btn = button instanceof HTMLButtonElement ? button : null;
  const originalHtml = btn?.innerHTML || (WAND_ICON_SVG + ' Auto-detect');
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = WAND_ICON_SVG + ' Detecting…';
  }
  if (statusEl) statusEl.textContent = 'Analysing image…';

  try {
    const res = await fetch(API_BASE + '/api/admin/images/analyze', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + getAuthToken()
      },
      body: JSON.stringify(payload)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Auto-detect failed');

    if (data.source !== 'ai' || data.success === false) {
      const message = 'Auto-detect failed';
      if (statusEl) statusEl.textContent = message;
      showToast(message, 'error');
      return;
    }

    if (titleInput && data.title) titleInput.value = data.title;
    if (descriptionInput && typeof data.description === 'string' && !/^\s*[\[{]/.test(data.description)) {
      descriptionInput.value = data.description;
    }
    if (categorySelect && data.category) selectCategoryOption(categorySelect, data.category);
    const tags = sanitizeTagList(data.tags || []);
    if (tagsInput && tags.length > 0) tagsInput.value = tags.join(', ');

    if (statusEl) statusEl.textContent = 'Detected from image — review before saving.';
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Auto-detect failed';
    if (statusEl) statusEl.textContent = message;
    showToast(message, 'error');
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = originalHtml;
    }
  }
}

/**
 * Preselects the upload form's category from the Images tab filter.
 *
 * Runs when the modal opens rather than once at start-up, because closing it
 * calls `form.reset()` and the filter may have moved since the last upload.
 */
function applyDefaultUploadCategory() {
  const filter = /** @type {HTMLSelectElement|null} */ (document.getElementById('category-filter'));
  const select = /** @type {HTMLSelectElement|null} */ (document.getElementById('edit-category'));
  if (!select) return;

  const available = [...select.options]
    .map((option) => option.value)
    .filter((value) => value && !Object.values(CATALOG_ACTIONS).includes(value));

  selectCategoryOption(select, defaultUploadCategory(filter?.value, available));
}

/**
 * Selects a category in a dropdown, inserting the option when it is unknown.
 *
 * @param {HTMLSelectElement} select
 * @param {string} name
 */
function selectCategoryOption(select, name) {
  const exists = [...select.options].some((option) => option.value === name);
  if (!exists) {
    const group = select.querySelector('optgroup[label="Categories"]') || select;
    const option = document.createElement('option');
    option.value = name;
    option.textContent = name;
    const sibling = [...group.children].find((child) => {
      const text = child.textContent || '';
      return text.localeCompare(name, undefined, { sensitivity: 'base' }) > 0;
    });
    if (sibling) {
      group.insertBefore(option, sibling);
    } else {
      group.appendChild(option);
    }
  }
  select.value = name;
  select.dataset.lastValue = name;
}

/**
 * Appends a tag to a comma separated tags input, sanitizing and deduping.
 *
 * @param {string} inputId
 * @param {string} tag
 */
function appendTagToInput(inputId, tag) {
  const input = /** @type {HTMLInputElement|null} */ (document.getElementById(inputId));
  if (!input) return;
  const merged = sanitizeTagList(input.value + ',' + tag);
  input.value = merged.join(', ');
}

/** Opens the Catalog tab in a new browser tab. */
function openCatalogTab() {
  window.open(window.location.pathname + '?tab=catalog', '_blank', 'noopener');
}

/** @type {((name: string) => void)|null} */
let catalogAddCallback = null;
/** @type {'category'|'tag'} */
let catalogAddKind = 'category';
/** @type {HTMLElement|null} */
let catalogAddReturnFocus = null;

/**
 * Opens the quick-add modal for a category or tag.
 *
 * @param {'category'|'tag'} kind
 * @param {(name: string) => void} [onCreated]
 */
function openCatalogAddModal(kind, onCreated) {
  catalogAddKind = kind;
  catalogAddCallback = onCreated || null;
  catalogAddReturnFocus = /** @type {HTMLElement|null} */ (document.activeElement);

  const modal = document.getElementById('catalog-add-modal');
  const title = document.getElementById('catalog-add-title');
  const label = document.getElementById('catalog-add-label');
  const preview = document.getElementById('catalog-add-preview');
  const input = /** @type {HTMLInputElement|null} */ (document.getElementById('catalog-add-input'));

  if (title) title.textContent = kind === 'category' ? 'Add new category' : 'Add new tag';
  if (label) label.textContent = kind === 'category' ? 'Category name' : 'Tag name';
  if (input) {
    input.value = '';
    input.placeholder = kind === 'category' ? 'e.g. Deep Space' : 'e.g. deep-space';
  }
  if (preview) preview.textContent = '';
  modal?.classList.add('open');
  input?.focus();
}

/** Closes the quick-add modal and restores focus. */
function closeCatalogAddModal() {
  document.getElementById('catalog-add-modal')?.classList.remove('open');
  catalogAddCallback = null;
  catalogAddReturnFocus?.focus();
  catalogAddReturnFocus = null;
}

/**
 * Creates a category or tag, sanitizing the name first.
 *
 * @param {'category'|'tag'} kind
 * @param {string} rawName
 * @returns {Promise<string|null>} Stored name, or null when the request failed.
 */
async function createCatalogEntry(kind, rawName) {
  const name = kind === 'category' ? sanitizeCategoryName(rawName) : sanitizeTagName(rawName);
  if (!name) {
    showToast(kind === 'category' ? 'Enter a category name' : 'Enter a tag name', 'error');
    return null;
  }

  const path = kind === 'category' ? '/api/admin/catalog/categories' : '/api/admin/catalog/tags';
  const res = await fetch(API_BASE + path, {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ' + getAuthToken(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ name })
  });

  if (res.status === 409) {
    showToast((kind === 'category' ? 'Category' : 'Tag') + ' "' + name + '" already exists', 'success');
    return name;
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    showToast(data.error || 'Failed to add ' + kind, 'error');
    return null;
  }

  const stored = typeof data.name === 'string' && data.name ? data.name : name;
  showToast((kind === 'category' ? 'Category' : 'Tag') + ' "' + stored + '" added', 'success');
  await fetchCatalog();
  return stored;
}

/** Wires the quick-add modal controls. */
function initCatalogAddModal() {
  const form = document.getElementById('catalog-add-form');
  const input = /** @type {HTMLInputElement|null} */ (document.getElementById('catalog-add-input'));
  const preview = document.getElementById('catalog-add-preview');

  input?.addEventListener('input', () => {
    if (!preview) return;
    const sanitized = catalogAddKind === 'category'
      ? sanitizeCategoryName(input.value)
      : sanitizeTagName(input.value);
    preview.textContent = sanitized ? 'Saved as: ' + sanitized : '';
  });

  document.getElementById('close-catalog-add-modal')?.addEventListener('click', () => closeCatalogAddModal());
  document.getElementById('cancel-catalog-add-btn')?.addEventListener('click', () => closeCatalogAddModal());

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (!document.getElementById('catalog-add-modal')?.classList.contains('open')) return;
    closeCatalogAddModal();
  });

  form?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const callback = catalogAddCallback;
    const stored = await createCatalogEntry(catalogAddKind, input?.value || '');
    if (!stored) return;
    closeCatalogAddModal();
    callback?.(stored);
  });
}

/**
 * Wires the Add / Manage actions shared by every category and tag dropdown.
 */
function initCatalogDropdownActions() {
  ['category-filter', 'edit-category'].forEach((id) => {
    const select = /** @type {HTMLSelectElement|null} */ (document.getElementById(id));
    if (!select) return;
    select.dataset.lastValue = select.value;
    select.addEventListener('change', (e) => {
      const value = select.value;
      if (value !== CATALOG_ACTIONS.addCategory && value !== CATALOG_ACTIONS.manageCategories) {
        select.dataset.lastValue = value;
        return;
      }
      // Keep filter/refresh listeners from ever seeing a sentinel value.
      e.stopImmediatePropagation();
      select.value = select.dataset.lastValue || (id === 'category-filter' ? 'All' : '');
      if (value === CATALOG_ACTIONS.manageCategories) {
        openCatalogTab();
        return;
      }
      openCatalogAddModal('category', (name) => {
        selectCategoryOption(select, name);
        if (id === 'category-filter') refreshImages();
      });
    });
  });

  bindTagSuggest('edit-tags-picker', 'edit-tags-search', 'edit-tags-suggest-list', 'edit-tags');
}

/**
 * Loads categories and tags, then refreshes every catalog-driven control.
 * @returns {Promise<void>}
 */
async function fetchCatalog() {
  const token = getAuthToken();
  try {
    const res = await fetch(API_BASE + '/api/admin/catalog', {
      headers: { 'Authorization': 'Bearer ' + token }
    });
    if (res.status === 401) {
      showToast('Unauthorized: Check Admin Secret Key', 'error');
    } else {
      const data = await res.json();
      catalogCategories = (data.categories || []).sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
      );
      catalogTags = (data.tags || []).sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
      );
    }
  } catch (err) {
    console.error('Failed to fetch catalog:', err);
    showToast('Failed to load catalog', 'error');
  }

  // Categories always fall back to the bundled defaults so the dropdowns work.
  if (catalogCategories.length === 0) {
    const sortedDefaults = [...DEFAULT_CATEGORIES].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
    catalogCategories = sortedDefaults.map((name) => ({ id: 'cat_' + name.toLowerCase(), name }));
  }
  renderCatalog(catalogCategories, catalogTags);
  populateCategorySelects(catalogCategories);
  populateTagSelects(catalogTags);
}

/**
 * Re-renders the catalog lists using the current filter inputs.
 */
function renderFilteredCatalog() {
  const catQuery = (/** @type {HTMLInputElement|null} */ (document.getElementById('catalog-category-search'))?.value || '');
  const tagQuery = (/** @type {HTMLInputElement|null} */ (document.getElementById('catalog-tag-search'))?.value || '');
  renderCatalog(
    catalogCategories.filter((c) => matchesSearch([c.name], catQuery)),
    catalogTags.filter((t) => matchesSearch([t.name], tagQuery))
  );
}

/**
 * Renames a category or tag; renames cascade to linked images server-side.
 *
 * @param {'categories'|'tags'} kind
 * @param {string} id
 * @param {string} name
 * @returns {Promise<void>}
 */
async function renameCatalogEntry(kind, id, name) {
  const res = await fetch(API_BASE + '/api/admin/catalog/' + kind + '/' + encodeURIComponent(id), {
    method: 'PUT',
    headers: {
      'Authorization': 'Bearer ' + getAuthToken(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ name })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    showToast(data.error || 'Failed to rename', 'error');
    return;
  }
  const stored = typeof data.name === 'string' && data.name ? data.name : name;
  const updated = data.imagesUpdated != null ? ' (' + data.imagesUpdated + ' images updated)' : '';
  showToast('Renamed to "' + stored + '"' + updated, 'success');
  await fetchCatalog();
  refreshImages();
}

/**
 * Deletes a category or tag after confirmation.
 *
 * @param {'categories'|'tags'} kind
 * @param {string} id
 * @param {string} name
 * @returns {Promise<void>}
 */
async function deleteCatalogEntry(kind, id, name) {
  const label = kind === 'categories' ? 'category' : 'tag';
  if (!confirm('Delete the ' + label + ' "' + name + '"?')) return;
  const res = await fetch(API_BASE + '/api/admin/catalog/' + kind + '/' + encodeURIComponent(id), {
    method: 'DELETE',
    headers: { 'Authorization': 'Bearer ' + getAuthToken() }
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    showToast(data.error || 'Failed to delete ' + label, 'error');
    return;
  }
  showToast(label.charAt(0).toUpperCase() + label.slice(1) + ' deleted', 'success');
  await fetchCatalog();
}

/**
 * @param {Array<{id:string,name:string}>} categories
 * @param {Array<{id:string,name:string}>} tags
 */
function renderCatalog(categories, tags) {
  const catList = document.getElementById('catalog-categories-list');
  const tagList = document.getElementById('catalog-tags-list');
  const countText = document.getElementById('catalog-count-text');

  if (countText) {
    const totalCats = catalogCategories.length;
    const totalTags = catalogTags.length;
    countText.textContent =
      totalCats + ' categor' + (totalCats === 1 ? 'y' : 'ies') +
      ' · ' +
      totalTags + ' tag' + (totalTags === 1 ? '' : 's');
  }

  if (catList) {
    catList.innerHTML = categories.map((c) => catalogRowHtml('category', c)).join('') ||
      '<li class="catalog-empty">No categories</li>';

    catList.querySelectorAll('.rename-category-btn').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        const el = /** @type {HTMLElement} */ (e.currentTarget);
        const id = el.getAttribute('data-id');
        const currentName = el.getAttribute('data-name') || '';
        if (!id) return;
        const next = prompt('Rename category:', currentName);
        if (next === null) return;
        const name = sanitizeCategoryName(next);
        if (!name || name === currentName) return;
        await renameCatalogEntry('categories', id, name);
      });
    });

    catList.querySelectorAll('.delete-category-btn').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        const el = /** @type {HTMLElement} */ (e.currentTarget);
        const id = el.getAttribute('data-id');
        if (!id) return;
        await deleteCatalogEntry('categories', id, el.getAttribute('data-name') || '');
      });
    });
  }

  if (tagList) {
    tagList.innerHTML = tags.map((t) => catalogRowHtml('tag', t)).join('') ||
      '<li class="catalog-empty">No tags yet</li>';

    tagList.querySelectorAll('.rename-tag-btn').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        const el = /** @type {HTMLElement} */ (e.currentTarget);
        const id = el.getAttribute('data-id');
        const currentName = el.getAttribute('data-name') || '';
        if (!id) return;
        const next = prompt('Rename tag:', currentName);
        if (next === null) return;
        const name = sanitizeTagName(next);
        if (!name || name === currentName) return;
        await renameCatalogEntry('tags', id, name);
      });
    });

    tagList.querySelectorAll('.delete-tag-btn').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        const el = /** @type {HTMLElement} */ (e.currentTarget);
        const id = el.getAttribute('data-id');
        if (!id) return;
        await deleteCatalogEntry('tags', id, el.getAttribute('data-name') || '');
      });
    });
  }
}

/**
 * Rebuilds every category dropdown, including the Add and Manage actions.
 *
 * @param {Array<{name:string}>} categories
 */
function populateCategorySelects(categories) {
  const names = categories.map((c) => c.name);
  ['edit-category', 'category-filter'].forEach((id) => {
    const select = /** @type {HTMLSelectElement|null} */ (document.getElementById(id));
    if (!select) return;
    const current = select.value;
    select.innerHTML = categoryOptionsHtml(names, id === 'category-filter');
    if ([...select.options].some((o) => o.value === current)) {
      select.value = current;
    }
    select.dataset.lastValue = select.value;
  });
}

/** @type {string[]} */
let editTagNames = [];

/**
 * Type-to-search tag field. Add and Manage stay at the bottom of the suggestions.
 *
 * @param {string} pickerId
 * @param {string} searchId
 * @param {string} listId
 * @param {string} tagsInputId
 */
function bindTagSuggest(pickerId, searchId, listId, tagsInputId) {
  const picker = document.getElementById(pickerId);
  const search = /** @type {HTMLInputElement|null} */ (document.getElementById(searchId));
  const list = document.getElementById(listId);
  if (!picker || !search || !list) return;

  const render = () => {
    const items = tagSuggestItems(editTagNames, search.value);
    list.innerHTML = items.map((item, index) => {
      const action = item.kind === 'action' ? ' tag-suggest-action' : '';
      return '<li role="presentation"><button type="button" tabindex="-1" class="tag-suggest-option' + action + '" role="option"' +
        ' data-value="' + escapeHtml(item.value) + '" id="' + listId + '-opt-' + index + '">' +
        escapeHtml(item.label) + '</button></li>';
    }).join('');
  };

  const open = () => {
    render();
    list.hidden = false;
    search.setAttribute('aria-expanded', 'true');
  };

  const close = () => {
    list.hidden = true;
    search.setAttribute('aria-expanded', 'false');
    search.removeAttribute('aria-activedescendant');
  };

  /**
   * @param {string} value
   */
  const choose = (value) => {
    search.value = '';
    close();
    if (value === CATALOG_ACTIONS.manageTags) {
      openCatalogTab();
      return;
    }
    if (value === CATALOG_ACTIONS.addTag) {
      openCatalogAddModal('tag', (name) => appendTagToInput(tagsInputId, name));
      return;
    }
    appendTagToInput(tagsInputId, value);
  };

  /**
   * @param {number} delta
   */
  const moveActive = (delta) => {
    if (list.hidden) open();
    const options = [...list.querySelectorAll('[role="option"]')];
    if (!options.length) return;
    const current = options.findIndex((el) => el.getAttribute('aria-selected') === 'true');
    const next = delta > 0
      ? (current + 1) % options.length
      : (current <= 0 ? options.length - 1 : current - 1);
    options.forEach((el, i) => el.setAttribute('aria-selected', i === next ? 'true' : 'false'));
    const active = options[next];
    if (active?.id) search.setAttribute('aria-activedescendant', active.id);
    if (active instanceof HTMLElement) active.scrollIntoView({ block: 'nearest' });
  };

  /**
   * Tab leaves the suggestion list instead of walking its buttons.
   * @param {boolean} backwards
   */
  const tabOut = (backwards) => {
    close();
    const root = search.closest('form') || document.body;
    const focusable = [...root.querySelectorAll('a, button, input, select, textarea')].filter((el) => {
      if (!(el instanceof HTMLElement)) return false;
      if (el.tabIndex < 0 || list.contains(el)) return false;
      if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement) {
        return !el.disabled;
      }
      return true;
    });
    const index = focusable.indexOf(search);
    const target = focusable[backwards ? index - 1 : index + 1];
    if (target instanceof HTMLElement) target.focus();
  };

  search.addEventListener('focus', open);
  search.addEventListener('input', open);
  search.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      close();
      return;
    }
    if (e.key === 'Tab') {
      close();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      moveActive(e.key === 'ArrowDown' ? 1 : -1);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (list.hidden) open();
      const options = [...list.querySelectorAll('[role="option"]')];
      const current = options.findIndex((el) => el.getAttribute('aria-selected') === 'true');
      const picked = options[current] || null;
      if (!picked) return;
      choose(picked.getAttribute('data-value') || '');
    }
  });

  list.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      moveActive(e.key === 'ArrowDown' ? 1 : -1);
      search.focus();
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      tabOut(e.shiftKey);
    }
  });

  list.addEventListener('mousedown', (e) => e.preventDefault());
  list.addEventListener('click', (e) => {
    const btn = /** @type {HTMLElement|null} */ (e.target instanceof Element ? e.target.closest('[data-value]') : null);
    if (!btn) return;
    choose(btn.getAttribute('data-value') || '');
  });

  document.addEventListener('mousedown', (e) => {
    if (e.target instanceof Node && picker.contains(e.target)) return;
    close();
  });
}

/**
 * Rebuilds the tag autocomplete from the catalog.
 *
 * @param {Array<{name:string}>} tags
 */
function populateTagSelects(tags) {
  editTagNames = tags.map((t) => t.name);
  const search = /** @type {HTMLInputElement|null} */ (document.getElementById('edit-tags-search'));
  const list = document.getElementById('edit-tags-suggest-list');
  if (!search || !list || list.hidden) return;
  search.dispatchEvent(new Event('input'));
}

function initCatalogHandlers() {
  /**
   * Wires an inline "add" row: live sanitized preview plus create on click/Enter.
   *
   * @param {'category'|'tag'} kind
   * @param {string} inputId
   * @param {string} buttonId
   * @param {string} previewId
   */
  const initAddRow = (kind, inputId, buttonId, previewId) => {
    const input = /** @type {HTMLInputElement|null} */ (document.getElementById(inputId));
    const preview = document.getElementById(previewId);
    const sanitize = kind === 'category' ? sanitizeCategoryName : sanitizeTagName;

    input?.addEventListener('input', () => {
      if (!preview) return;
      const sanitized = sanitize(input.value);
      preview.textContent = sanitized && sanitized !== input.value.trim() ? 'Saved as: ' + sanitized : '';
    });

    const submit = async () => {
      if (!input?.value.trim()) return;
      const stored = await createCatalogEntry(kind, input.value);
      if (!stored) return;
      input.value = '';
      if (preview) preview.textContent = '';
    };

    document.getElementById(buttonId)?.addEventListener('click', submit);
    input?.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      submit();
    });
  };

  initAddRow('category', 'new-category-input', 'add-category-btn', 'new-category-preview');
  initAddRow('tag', 'new-tag-input', 'add-tag-btn', 'new-tag-preview');

  document.getElementById('catalog-category-search')?.addEventListener('input', () => renderFilteredCatalog());
  document.getElementById('catalog-tag-search')?.addEventListener('input', () => renderFilteredCatalog());
}

/**
 * Measures sticky header height into --admin-header-offset.
 */
function updateAdminHeaderOffset() {
  const header = document.querySelector('header');
  if (!header) return;
  const height = Math.ceil(/** @type {HTMLElement} */(header).getBoundingClientRect().height);
  document.documentElement.style.setProperty('--admin-header-offset', height + 'px');
}

document.addEventListener('DOMContentLoaded', () => {
  fillGridPresetSelects();
  updateAdminHeaderOffset();
  window.addEventListener('resize', updateAdminHeaderOffset);

  installUnauthorizedInterceptor();
  initAdminLogin();
  bindPageHelpToggles();

  // Registered before the filter listeners so sentinel options never trigger a fetch.
  const initialCategories = [...DEFAULT_CATEGORIES]
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    .map((name) => ({ name }));
  populateCategorySelects(initialCategories);
  populateTagSelects([]);
  initCatalogDropdownActions();
  initCatalogAddModal();

  // Image filters + view toggle
  document.getElementById('search-filter')?.addEventListener('input', () => refreshImages());
  document.getElementById('category-filter')?.addEventListener('change', () => refreshImages());
  document.getElementById('image-status-filter')?.addEventListener('change', () => refreshImages());
  document.getElementById('image-uploaded-by-filter')?.addEventListener('change', () => refreshImages());
  document.getElementById('image-nsfw-filter')?.addEventListener('change', () => refreshImages());
  document.getElementById('images-view-grid-btn')?.addEventListener('click', () => setImagesViewMode('grid'));
  document.getElementById('images-view-table-btn')?.addEventListener('click', () => setImagesViewMode('table'));

  // Room filters & sorting
  document.getElementById('room-search-filter')?.addEventListener('input', () => refreshRooms());
  document.getElementById('room-activity-filter')?.addEventListener('change', () => refreshRooms());
  document.getElementById('room-visibility-filter')?.addEventListener('change', () => refreshRooms());
  document.getElementById('publish-room-cancel')?.addEventListener('click', () => closePublishRoomDialog());
  document.getElementById('publish-room-close')?.addEventListener('click', () => closePublishRoomDialog());
  document.getElementById('publish-room-confirm')?.addEventListener('click', () => confirmPublishRoom());
  document.getElementById('room-sort-select')?.addEventListener('change', () => refreshRooms());
  document.getElementById('room-order-select')?.addEventListener('change', () => refreshRooms());

  // User filters & sorting
  document.getElementById('user-search-filter')?.addEventListener('input', () => refreshUsers());
  document.getElementById('user-status-filter')?.addEventListener('change', () => refreshUsers());
  document.getElementById('user-type-filter')?.addEventListener('change', () => refreshUsers());
  document.getElementById('user-sort-select')?.addEventListener('change', () => refreshUsers());
  document.getElementById('user-order-select')?.addEventListener('change', () => refreshUsers());

  // Flags filters
  document.getElementById('flagged-status-filter')?.addEventListener('change', () => refreshFlaggedMessages());
  document.getElementById('flagged-entity-filter')?.addEventListener('change', () => refreshFlaggedMessages());

  // Conversation filters
  document.getElementById('messages-status-filter')?.addEventListener('change', () => refreshConversations());
  document.getElementById('messages-search-filter')?.addEventListener('input', () => refreshConversations());

  // Settings
  document.getElementById('save-settings-btn')?.addEventListener('click', () => saveSettings());

  initTabs();
  initAdminChats();
  initUploadHandlers();
  initEditHandlers();
  initCatalogHandlers();
  initErrorsHandlers();
  initConversationHandlers();
  initSettingsDirtyTracking();
  initDashboardControls();
  // Reads each table's own headers, so a column added later needs no registration.
  initColumnChoosers();
  applyImagesViewMode();
  mountPagination('images-pagination', imagesPageMeta, (next) => {
    imagesPage = next;
    fetchImages();
  });
  mountPagination('rooms-pagination', roomsPageMeta, (next) => {
    roomsPage = next;
    fetchRooms();
  });
  mountPagination('users-pagination', usersPageMeta, (next) => {
    usersPage = next;
    fetchUsers();
  });
  mountPagination('flagged-pagination', flagsPageMeta, (next) => {
    flagsPage = next;
    fetchFlaggedMessages();
  });
  mountPagination('errors-pagination', errorsPageMeta, (next) => {
    errorsPage = next;
    fetchClientErrors();
  });
  mountPagination('messages-pagination', conversationsPageMeta, (next) => {
    conversationsPage = next;
    fetchConversations();
  });

  // Dashboard drill-down navigation handlers
  window.addEventListener('dashboard:navigate-tag', (e) => {
    const tagName = /** @type {CustomEvent} */ (e).detail;
    switchTab('images', { pushState: true, skipRefresh: true });
    resetImageFilters();
    const searchFilter = /** @type {HTMLInputElement|null} */ (document.getElementById('search-filter'));
    if (searchFilter) {
      searchFilter.value = String(tagName || '');
    }
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', 'images');
      url.searchParams.set('tag', String(tagName || ''));
      url.searchParams.delete('image_id');
      url.searchParams.delete('id');
      url.searchParams.delete('category');
      url.searchParams.delete('q');
      window.history.pushState({ tab: 'images', tag: tagName }, '', url.toString());
    } catch {}
    refreshImages();
  });

  window.addEventListener('dashboard:navigate-category', (e) => {
    const catName = /** @type {CustomEvent} */ (e).detail;
    switchTab('images', { pushState: true, skipRefresh: true });
    resetImageFilters();
    const catFilter = /** @type {HTMLSelectElement|null} */ (document.getElementById('category-filter'));
    if (catFilter) {
      const targetCat = String(catName || '');
      if (targetCat && ![...catFilter.options].some((o) => o.value.toLowerCase() === targetCat.toLowerCase())) {
        const opt = document.createElement('option');
        opt.value = targetCat;
        opt.textContent = targetCat;
        catFilter.appendChild(opt);
      }
      catFilter.value = targetCat || 'All';
    }
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', 'images');
      url.searchParams.set('category', String(catName || ''));
      url.searchParams.delete('image_id');
      url.searchParams.delete('id');
      url.searchParams.delete('tag');
      url.searchParams.delete('q');
      window.history.pushState({ tab: 'images', category: catName }, '', url.toString());
    } catch {}
    refreshImages();
  });

  window.addEventListener('dashboard:navigate-image', (e) => {
    const imgId = String(/** @type {CustomEvent} */ (e).detail || '').trim();
    switchTab('images', { pushState: true, skipRefresh: true });
    resetImageFilters();
    const searchFilter = /** @type {HTMLInputElement|null} */ (document.getElementById('search-filter'));
    if (searchFilter) {
      searchFilter.value = imgId;
    }
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', 'images');
      url.searchParams.set('image_id', imgId);
      url.searchParams.delete('category');
      url.searchParams.delete('tag');
      url.searchParams.delete('q');
      window.history.pushState({ tab: 'images', image_id: imgId }, '', url.toString());
    } catch {}
    refreshImages();
  });

  window.addEventListener('dashboard:navigate-catalog', () => {
    switchTab('images', { pushState: true, skipRefresh: true });
    resetImageFilters();
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', 'images');
      url.searchParams.delete('image_id');
      url.searchParams.delete('category');
      url.searchParams.delete('tag');
      url.searchParams.delete('q');
      window.history.pushState({ tab: 'images' }, '', url.toString());
    } catch {}
    refreshImages();
  });

  window.addEventListener('dashboard:navigate-active-users', () => {
    switchTab('users', { pushState: true, skipRefresh: true });
    resetUserFilters();
    const statusSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('user-status-filter'));
    if (statusSelect) statusSelect.value = 'active';
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', 'users');
      url.searchParams.set('status', 'active');
      url.searchParams.delete('q');
      window.history.pushState({ tab: 'users', status: 'active' }, '', url.toString());
    } catch {}
    refreshUsers();
  });

  window.addEventListener('dashboard:navigate-user', (e) => {
    const detail = /** @type {CustomEvent} */ (e).detail || {};
    const query = String(detail.username || detail.id || '').trim();
    switchTab('users', { pushState: true, skipRefresh: true });
    resetUserFilters();
    const statusSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('user-status-filter'));
    if (statusSelect) statusSelect.value = 'active';
    const searchFilter = /** @type {HTMLInputElement|null} */ (document.getElementById('user-search-filter'));
    if (searchFilter && query) {
      searchFilter.value = query;
    }
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', 'users');
      url.searchParams.set('status', 'active');
      if (query) url.searchParams.set('q', query);
      window.history.pushState({ tab: 'users', status: 'active', q: query }, '', url.toString());
    } catch {}
    refreshUsers();
  });

  window.addEventListener('dashboard:navigate-active-rooms', () => {
    switchTab('rooms', { pushState: true, skipRefresh: true });
    resetRoomFilters();
    const activitySelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('room-activity-filter'));
    if (activitySelect) activitySelect.value = 'active';
    const sortSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('room-sort-select'));
    if (sortSelect) sortSelect.value = 'active_sessions';
    const orderSelect = /** @type {HTMLSelectElement|null} */ (document.getElementById('room-order-select'));
    if (orderSelect) orderSelect.value = 'desc';
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', 'rooms');
      url.searchParams.set('activity', 'active');
      url.searchParams.set('sort', 'active_sessions');
      url.searchParams.set('order', 'desc');
      url.searchParams.delete('q');
      window.history.pushState({ tab: 'rooms', activity: 'active', sort: 'active_sessions', order: 'desc' }, '', url.toString());
    } catch {}
    refreshRooms();
  });

  window.addEventListener('dashboard:navigate-room', (e) => {
    const detail = /** @type {CustomEvent} */ (e).detail || {};
    const roomId = String(detail.id || '').trim();
    switchTab('rooms', { pushState: true, skipRefresh: true });
    resetRoomFilters();
    const searchFilter = /** @type {HTMLInputElement|null} */ (document.getElementById('room-search-filter'));
    if (searchFilter && roomId) {
      searchFilter.value = roomId;
    }
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', 'rooms');
      if (roomId) url.searchParams.set('q', roomId);
      window.history.pushState({ tab: 'rooms', q: roomId }, '', url.toString());
    } catch {}
    refreshRooms();
  });

  // Anomaly & Incident Alert Action Handler (Recommendation 3)
  window.addEventListener('dashboard:alert-action', (e) => {
    const detail = /** @type {CustomEvent} */ (e).detail || {};
    if (detail.target === 'errors') {
      switchTab('errors', { pushState: true, skipRefresh: true });
      const sourceEl = /** @type {HTMLSelectElement|null} */ (document.getElementById('errors-source-filter'));
      const statusEl = /** @type {HTMLSelectElement|null} */ (document.getElementById('errors-status-filter'));
      if (sourceEl) sourceEl.value = 'all';
      if (statusEl) statusEl.value = 'open';
      refreshClientErrors();
    } else if (detail.target === 'flags') {
      switchTab('moderation', { pushState: true });
    } else if (detail.target === 'health') {
      document.getElementById('dash-system-health-panel')?.scrollIntoView({ behavior: 'smooth' });
    }
  });

  // Clicking on Images overview card resets filters and opens Images tab
  document.getElementById('dash-images-card')?.addEventListener('click', () => {
    switchTab('images', { pushState: true, skipRefresh: true });
    resetImageFilters();
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', 'images');
      url.searchParams.delete('image_id');
      url.searchParams.delete('id');
      url.searchParams.delete('category');
      url.searchParams.delete('tag');
      url.searchParams.delete('q');
      window.history.pushState({ tab: 'images' }, '', url.toString());
    } catch {}
    refreshImages();
  });

  // Clicking on Users overview card resets filters and opens Users tab with active users
  document.getElementById('dash-users-card')?.addEventListener('click', () => {
    window.dispatchEvent(new CustomEvent('dashboard:navigate-active-users'));
  });

  // Clicking on Rooms overview card resets filters and opens Rooms tab
  document.getElementById('dash-rooms-card')?.addEventListener('click', () => {
    switchTab('rooms', { pushState: true, skipRefresh: true });
    resetRoomFilters();
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', 'rooms');
      url.searchParams.delete('activity');
      url.searchParams.delete('sort');
      url.searchParams.delete('order');
      url.searchParams.delete('q');
      window.history.pushState({ tab: 'rooms' }, '', url.toString());
    } catch {}
    refreshRooms();
  });

  // Clicking on Online Now card opens Rooms tab with active filter and sorted by active player count desc
  document.getElementById('dash-online')?.addEventListener('click', () => {
    window.dispatchEvent(new CustomEvent('dashboard:navigate-active-rooms'));
  });

  document.querySelectorAll('.dash-ops-item').forEach((item) => {
    item.addEventListener('click', (e) => {
      e.preventDefault();
      const targetTab = item.getAttribute('data-tab-link');
      if (targetTab) {
        const tabBtn = document.querySelector(`.nav-tab-btn[data-tab="${targetTab}"]`);
        if (tabBtn instanceof HTMLElement) tabBtn.click();
      }
    });
  });

  // No session means no API calls at all: the gate is the first thing rendered.
  if (!getAuthToken()) {
    showLoginGate();
    return;
  }
  if (activeTab === 'dashboard') {
    refreshDashboard();
    syncDashboardPolling(true);
  } else {
    refreshActiveTab();
  }
  fetchCatalog();
  loadGatedNav();
});
